import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { HERO_SEARCH_DEFAULTS } from '@utazzo/shared';
import { UzCombobox, UzInput, UzSwitch, UzTextarea } from '@utazzo/ui';
import {
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Copy,
  GripVertical,
  Package,
  Plus,
  Trash2,
} from 'lucide-react';
import React from 'react';

import { MediaDropField } from '../components/MediaDropField';
import { MediaGalleryField } from '../components/MediaGalleryField';
import { RichTextEditor } from '../components/RichTextEditor';

import { DestinationMultiSelect } from './components/DestinationMultiSelect';
import { HeroInclusionChecklist } from './components/HeroInclusionChecklist';
import { IconPicker } from './components/IconPicker';
import { type LookupOption, RemoteLookup } from './components/RemoteLookup';

import { OrderedPickerList } from '@/components/ordered-picker/OrderedPickerList';
import {
  ReelCheckBar,
  reelCheckBorderClass,
  reelCheckError,
  reelCheckProvider,
  useReelCheck,
} from '@/components/reel-check/ReelCheck';
import { useBranchesList } from '@/features/branches/use-branches';
import { useCategoryPagesList } from '@/features/category-pages/use-category-pages';
import { useDestinationsList } from '@/features/destinations/use-destinations';
import { useMarketingTagsList } from '@/features/marketing-tags/use-marketing-tags';
import { usePackagesList } from '@/features/packages/use-packages';

/**
 * Per-type section content forms. Each form edits the snake_case dataJson
 * shape defined by the matching Zod schema in @utazzo/shared. Image fields
 * use the inline <MediaDropField> (drag-drop upload → stores media id).
 *
 * Contract: each form receives the section's `data` object + an `onChange`
 * that replaces it. The editor persists (PATCH /admin/website/sections/:id)
 * debounced on change.
 */

type Data = Record<string, unknown>;
interface FormProps {
  data: Data;
  onChange: (next: Data) => void;
  disabled?: boolean;
}

// ── field primitives ────────────────────────────────────────────────────────
const Text = ({
  label,
  value,
  onChange,
  placeholder,
  disabled,
  error,
  inputClassName,
}: {
  label: string;
  value: unknown;
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Field-level message; also turns the input's border red. */
  error?: string;
  /** Extra border/state classes (e.g. the reel-check green/amber). */
  inputClassName?: string;
}): React.JSX.Element => (
  <div>
    <label className="mb-1 block text-xs font-medium text-(--fg-muted)">{label}</label>
    <UzInput
      value={typeof value === 'string' ? value : ''}
      placeholder={placeholder}
      disabled={disabled}
      error={error}
      className={inputClassName}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

const Area = ({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: unknown;
  onChange: (v: string) => void;
  disabled?: boolean;
}): React.JSX.Element => (
  <div>
    <label className="mb-1 block text-xs font-medium text-(--fg-muted)">{label}</label>
    <UzTextarea
      value={typeof value === 'string' ? value : ''}
      rows={3}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

const Color = ({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: unknown;
  onChange: (v: string) => void;
  disabled?: boolean;
}): React.JSX.Element => {
  const v = typeof value === 'string' && /^#[0-9a-fA-F]{6}$/.test(value) ? value : '#093F51';
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-(--fg-muted)">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={v}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-12 cursor-pointer rounded-md border border-(--border)"
        />
        <UzInput value={v} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
      </div>
    </div>
  );
};

/** Generic repeater for `items` arrays. */
/** Dashed "add" button, shared by every list in this file. */
const AddButton = ({
  label,
  onClick,
  className = '',
}: {
  label: string;
  onClick: () => void;
  className?: string;
}): React.JSX.Element => (
  <button
    type="button"
    onClick={onClick}
    className={`flex items-center justify-center gap-1.5 rounded-md border border-dashed border-(--border-strong) py-2 text-xs font-medium text-(--fg-muted) hover:border-(--primary) hover:text-(--primary) ${className}`}
  >
    <Plus className="h-3.5 w-3.5" /> {label}
  </button>
);

/**
 * Move up / move down / duplicate / remove for one row of an ordered list.
 *
 * Extracted so the grouped places editor drives the list by exactly the same
 * rules as the plain repeater - the two disagreeing about what "move down" does
 * at a boundary is the kind of bug nobody reports, they just stop trusting the
 * arrows.
 */
const ItemControls = ({
  index,
  items,
  onItems,
  noun = 'item',
  canDuplicate = true,
  removeTitle,
}: {
  index: number;
  items: Data[];
  onItems: (next: Data[]) => void;
  /** What this row is, for the button labels ("item", "place", "section"). */
  noun?: string;
  canDuplicate?: boolean;
  /** Overrides the remove tooltip where the consequence needs spelling out. */
  removeTitle?: string;
}): React.JSX.Element => (
  <div className="flex items-center gap-2">
    <button
      type="button"
      className="text-(--fg-faint) hover:text-(--fg) disabled:cursor-not-allowed disabled:opacity-30"
      disabled={index === 0}
      onClick={() => {
        const next = [...items];
        [next[index - 1], next[index]] = [next[index]!, next[index - 1]!];
        onItems(next);
      }}
      aria-label={`Move ${noun} up`}
      title="Move up"
    >
      <ChevronUp className="h-3.5 w-3.5" />
    </button>
    <button
      type="button"
      className="text-(--fg-faint) hover:text-(--fg) disabled:cursor-not-allowed disabled:opacity-30"
      disabled={index === items.length - 1}
      onClick={() => {
        const next = [...items];
        [next[index], next[index + 1]] = [next[index + 1]!, next[index]!];
        onItems(next);
      }}
      aria-label={`Move ${noun} down`}
      title="Move down"
    >
      <ChevronDown className="h-3.5 w-3.5" />
    </button>
    {canDuplicate && (
      <button
        type="button"
        className="text-(--fg-faint) hover:text-(--primary)"
        onClick={() => {
          const next = [...items];
          // Insert a copy right after this item so it's easy to tweak.
          next.splice(index + 1, 0, { ...items[index]! });
          onItems(next);
        }}
        aria-label={`Duplicate ${noun}`}
        title="Duplicate"
      >
        <Copy className="h-3.5 w-3.5" />
      </button>
    )}
    <button
      type="button"
      className="text-(--fg-faint) hover:text-(--ds-error)"
      onClick={() => onItems(items.filter((_, idx) => idx !== index))}
      aria-label={`Remove ${noun}`}
      title={removeTitle ?? 'Remove'}
    >
      <Trash2 className="h-3.5 w-3.5" />
    </button>
  </div>
);

function Repeater({
  items,
  onItems,
  blank,
  disabled,
  max,
  render,
}: {
  items: Data[];
  onItems: (next: Data[]) => void;
  blank: () => Data;
  disabled?: boolean;
  /** Optional cap - hides the "Add item" button once reached. */
  max?: number;
  render: (item: Data, set: (patch: Data) => void, index: number) => React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      {items.map((item, i) => (
        <div key={i} className="rounded-md border border-(--border) bg-(--z-50) p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-medium text-(--fg-faint)">Item {i + 1}</span>
            {!disabled && <ItemControls index={i} items={items} onItems={onItems} />}
          </div>
          <div className="flex flex-col gap-2">
            {render(
              item,
              (patch) => onItems(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it))),
              i,
            )}
          </div>
        </div>
      ))}
      {!disabled && (max === undefined || items.length < max) && (
        <AddButton label="Add item" onClick={() => onItems([...items, blank()])} />
      )}
    </div>
  );
}

const arr = (v: unknown): Data[] => (Array.isArray(v) ? (v as Data[]) : []);

/**
 * Plain text lifted out of editor HTML, for the plain-text mirror kept beside
 * every rich body (fallback for content authored before the editor, and the
 * only safe source anywhere the copy has to be text rather than markup).
 *
 * Goes through the DOM rather than a tag-stripping regex so entities decode;
 * block tags are padded first, or "…sunset.</p><p>Pair with…" runs two
 * sentences together.
 */
const htmlToPlainText = (html: string): string => {
  const el = document.createElement('div');
  el.innerHTML = html.replace(/<[/](?:p|div|li|h[1-6]|tr|blockquote)>/gi, ' $&');
  return (el.textContent ?? '').replace(/\s+/g, ' ').trim();
};

/**
 * Seed for a rich-text field whose content predates the editor: the legacy
 * plain text wrapped in paragraphs, so the editor opens on the copy that's
 * already live instead of a blank document. Only used while the HTML field is
 * still empty - once the author types, their HTML is what's stored.
 */
const plainTextToHtml = (text: string): string =>
  text
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map(
      (line) => `<p>${line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>`,
    )
    .join('');

/** Join a string[] into one-per-line textarea text. */
const linesToText = (v: unknown): string => (Array.isArray(v) ? (v as string[]).join('\n') : '');
/** Split textarea text back into a trimmed, blank-free string[]. */
const textToLines = (v: string): string[] =>
  v
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

/**
 * Every section's heading + subheading come from the section-level title /
 * subtitle fields (rendered above this form by SortableSectionCard), so the
 * per-type forms no longer carry their own heading inputs. This one-liner
 * points editors at the right place.
 */
const HeadingHint = (): React.JSX.Element => (
  <p className="text-[12px] text-(--fg-faint)">
    Heading &amp; subheading come from this section&apos;s title / subtitle fields above.
  </p>
);

const Toggle = ({
  label,
  hint,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}): React.JSX.Element => (
  <div className="flex items-center justify-between gap-4 rounded-md border border-(--border) bg-(--z-50) px-3 py-2.5">
    <div>
      <div className="text-sm font-medium text-(--fg)">{label}</div>
      {hint && <div className="text-[12px] text-(--fg-faint)">{hint}</div>}
    </div>
    <UzSwitch checked={checked} onCheckedChange={onChange} disabled={disabled} aria-label={label} />
  </div>
);

/** Segmented control - a single-choice picker rendered as a button group.
 * Industry-standard pattern for a small, fixed set of mutually-exclusive
 * options (e.g. a size/height preset). */
const Choice = <T extends string>({
  label,
  hint,
  value,
  options,
  onChange,
  disabled,
}: {
  label: string;
  hint?: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  disabled?: boolean;
}): React.JSX.Element => (
  <div>
    <label className="mb-1 block text-xs font-medium text-(--fg-muted)">{label}</label>
    <div className="flex gap-0.5 rounded-md border border-(--border) bg-(--z-50) p-0.5">
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            disabled={disabled}
            onClick={() => onChange(opt.value)}
            aria-pressed={active}
            className={`flex-1 rounded px-3 py-1.5 text-sm font-medium transition disabled:opacity-50 ${
              active
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-(--fg-muted) hover:text-(--fg)'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
    {hint && <p className="mt-1 text-[12px] text-(--fg-faint)">{hint}</p>}
  </div>
);

/**
 * The search the hero panel opens on for a fresh visit.
 *
 * A row saved before these fields existed carries none of them, and the website
 * opens it on `HERO_SEARCH_DEFAULTS` - so that is what an untouched form shows
 * too, rather than blanks that would not match the live page. `undefined` is
 * "never set"; an editor who clears a field stores `''`, which the website
 * respects as "leave this one empty".
 */
const HeroSearchPrefill = ({ data, onChange, disabled }: FormProps): React.JSX.Element => {
  const text = (v: unknown): string => (typeof v === 'string' ? v : '');
  const enabled = data.search_prefill_enabled !== false;
  const flights = data.flight_search_enabled === true;
  const hotels = data.hotel_search_enabled === true;

  const own = (key: string, fallback: string): string =>
    data[key] === undefined || data[key] === null ? fallback : text(data[key]);
  const from = own('search_default_from', HERO_SEARCH_DEFAULTS.from);
  const to = own('search_default_to', HERO_SEARCH_DEFAULTS.to);
  const fromLabel = own('search_default_from_label', HERO_SEARCH_DEFAULTS.fromLabel);
  const toLabel = own('search_default_to_label', HERO_SEARCH_DEFAULTS.toLabel);
  // The city's names belong to its id, so they only fall back together.
  const ownCity = data.search_default_hotel_city_id != null;
  const cityId = ownCity
    ? text(data.search_default_hotel_city_id)
    : HERO_SEARCH_DEFAULTS.hotelCityId;
  const cityName = ownCity
    ? [text(data.search_default_hotel_city_name), text(data.search_default_hotel_country_name)]
        .filter(Boolean)
        .join(', ')
    : `${HERO_SEARCH_DEFAULTS.hotelCityName}, ${HERO_SEARCH_DEFAULTS.hotelCountryName}`;
  const daysAhead =
    typeof data.search_default_days_ahead === 'number'
      ? data.search_default_days_ahead
      : HERO_SEARCH_DEFAULTS.daysAhead;

  const airport = (r: Data): LookupOption | null =>
    typeof r.iataCode === 'string' && r.iataCode.length === 3
      ? {
          id: r.iataCode,
          label: `${text(r.cityName)} (${r.iataCode})`,
          description: [text(r.airportName), text(r.countryName)].filter(Boolean).join(' · '),
          raw: r,
        }
      : null;

  const picked = (label: string, clear: () => void): React.JSX.Element => (
    <div className="flex items-center justify-between rounded-md border border-(--border) bg-(--z-50) px-3 py-2 text-sm">
      <span>{label}</span>
      {!disabled && (
        <button
          type="button"
          className="text-[12px] text-(--fg-faint) hover:text-(--fg)"
          onClick={clear}
        >
          Clear
        </button>
      )}
    </div>
  );

  return (
    <>
      <Toggle
        label="Open the search panel pre-filled"
        hint="A first-time visitor sees a ready-to-run search instead of empty boxes. Someone coming back from results still gets their own search, never this one."
        checked={enabled}
        onChange={(v) => onChange({ ...data, search_prefill_enabled: v })}
        disabled={disabled}
      />
      {enabled && (
        <div className="space-y-3 rounded-md border border-(--border) p-3">
          {flights && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <RemoteLookup
                    label="Flights - From"
                    placeholder="City or airport…"
                    path="/flights/airports"
                    toOption={airport}
                    onPick={(o) =>
                      onChange({
                        ...data,
                        search_default_from: o.id,
                        search_default_from_label: text(o.raw.cityName),
                      })
                    }
                    disabled={disabled}
                  />
                  {from
                    ? picked(`${fromLabel || from} (${from})`, () =>
                        onChange({
                          ...data,
                          search_default_from: '',
                          search_default_from_label: '',
                        }),
                      )
                    : null}
                </div>
                <div className="space-y-1.5">
                  <RemoteLookup
                    label="Flights - To"
                    placeholder="City or airport…"
                    path="/flights/airports"
                    toOption={airport}
                    onPick={(o) =>
                      onChange({
                        ...data,
                        search_default_to: o.id,
                        search_default_to_label: text(o.raw.cityName),
                      })
                    }
                    disabled={disabled}
                  />
                  {to
                    ? picked(`${toLabel || to} (${to})`, () =>
                        onChange({ ...data, search_default_to: '', search_default_to_label: '' }),
                      )
                    : null}
                </div>
              </div>
              {from !== '' && from === to && (
                <p className="text-[12px] text-red-600">
                  From and To are the same airport - the website will leave To empty.
                </p>
              )}
            </>
          )}
          {hotels && (
            <div className="space-y-1.5">
              <RemoteLookup
                label="Hotels - Destination"
                placeholder="Search a city…"
                path="/stays/cities"
                toOption={(r) =>
                  r.type === 'city' && typeof r.cityId === 'string'
                    ? {
                        id: r.cityId,
                        label: [text(r.name), text(r.countryName)].filter(Boolean).join(', '),
                        description: `${typeof r.hotelCount === 'number' ? r.hotelCount : 0} hotels synced from TBO`,
                        raw: r,
                      }
                    : null
                }
                onPick={(o) =>
                  onChange({
                    ...data,
                    search_default_hotel_city_id: o.id,
                    search_default_hotel_city_name: text(o.raw.name),
                    search_default_hotel_country_name: text(o.raw.countryName),
                  })
                }
                disabled={disabled}
              />
              {cityId
                ? picked(cityName || cityId, () =>
                    onChange({
                      ...data,
                      search_default_hotel_city_id: '',
                      search_default_hotel_city_name: '',
                      search_default_hotel_country_name: '',
                    }),
                  )
                : null}
            </div>
          )}
          <Num
            label="Date - days from today (1 = tomorrow)"
            value={daysAhead}
            onChange={(v) =>
              onChange({
                ...data,
                search_default_days_ahead: Math.min(60, Math.max(0, Math.round(v) || 0)),
              })
            }
            min={0}
            max={60}
            disabled={disabled}
          />
          <p className="text-[12px] text-(--fg-faint)">
            The date is never stored - the website works it out from each visitor&apos;s own today,
            so it moves forward every day. Flights open one-way on that date; hotels check in on it
            and check out one night later.
          </p>
        </div>
      )}
    </>
  );
};

// ── per-type forms ───────────────────────────────────────────────────────────
const Hero = ({ data, onChange, disabled }: FormProps): React.JSX.Element => {
  const statsEnabled = data.stats_enabled !== false;
  // Opt-in, so it defaults to OFF for every existing page.
  const hideSiteNav = data.hide_site_nav === true;
  // Opt-in too. The flight panel and the plain "what are you looking for?" box
  // occupy the same slot under the hero copy, so the flight widget wins and the
  // simple search is hidden while it is on.
  const flightSearch = data.flight_search_enabled === true;
  const hotelSearch = data.hotel_search_enabled === true;
  const activitiesSearch = data.activities_search_enabled === true;
  // Any product widget occupies the same slot as the plain search box, so
  // the box hides while flights, hotels or activities is on.
  const panelSearch = flightSearch || hotelSearch || activitiesSearch;
  const searchEnabled = !panelSearch && data.search_enabled !== false;
  return (
    <>
      <MediaDropField
        label="Background video"
        kind="video"
        value={data.video_media_id as string}
        onChange={(id) => onChange({ ...data, video_media_id: id })}
        disabled={disabled}
      />
      <Text
        label="…or external video URL (mp4/webm)"
        value={data.video_url}
        onChange={(v) => onChange({ ...data, video_url: v })}
        placeholder="https://ik.imagekit.io/…/hero.mp4"
        disabled={disabled}
      />
      <MediaDropField
        label="Fallback / poster image"
        kind="image"
        value={data.image_media_id as string}
        onChange={(id) => onChange({ ...data, image_media_id: id })}
        disabled={disabled}
      />
      <Choice
        label="Hero height"
        hint="How much of the screen the hero fills."
        value={
          (typeof data.height === 'string' ? data.height : 'full') as 'full' | 'half' | 'third'
        }
        options={[
          { value: 'full', label: 'Full screen' },
          { value: 'half', label: 'Banner' },
          { value: 'third', label: 'One third' },
        ]}
        onChange={(height) => onChange({ ...data, height })}
        disabled={disabled}
      />
      <Toggle
        label="Logo-only header (hide menu)"
        hint="Removes every header link - top bar, main menu, mobile search + hamburger - leaving just the logo. Use on landing pages so visitors stay on the campaign."
        checked={hideSiteNav}
        onChange={(v) => onChange({ ...data, hide_site_nav: v })}
        disabled={disabled}
      />
      <HeadingHint />
      <Text
        label="CTA text"
        value={data.cta_text}
        onChange={(v) => onChange({ ...data, cta_text: v })}
        placeholder="Request Call Back"
        disabled={disabled}
      />
      <Color
        label="CTA colour"
        value={(typeof data.cta_colour === 'string' && data.cta_colour) || '#FEE21C'}
        onChange={(v) => onChange({ ...data, cta_colour: v })}
        disabled={disabled}
      />
      <p className="text-[12px] text-(--fg-faint)">
        The CTA opens the site enquiry popup. Shown when search is off and CTA text is set.
      </p>

      {/* Show/hide only. Trip type, cabin, airports, dates, travellers and the
          special-fare chips are all the visitor's to pick on the website, so
          none of them are section settings. */}
      <Toggle
        label="Show flight search widget"
        hint="The flight booking panel under the hero copy - trip type, From / To airports, dates, travellers, cabin class and special fares. Airports come live from the City Master (any active city with an IATA code)."
        checked={flightSearch}
        onChange={(v) => onChange({ ...data, flight_search_enabled: v })}
        disabled={disabled}
      />
      <Toggle
        label="Show hotel search widget"
        hint="The hotel (stays) panel under the hero copy - destination, dates and rooms / guests. Stay cities come live from the City Master. Turn this on with flights to give the panel a Flights / Hotels tab switcher, or on its own for a hotels-only panel."
        checked={hotelSearch}
        onChange={(v) => onChange({ ...data, hotel_search_enabled: v })}
        disabled={disabled}
      />
      <Toggle
        label="Show activities tab"
        hint="Adds an Activities tab to the search panel. Activities booking is not live yet, so the tab shows a “coming soon” panel."
        checked={activitiesSearch}
        onChange={(v) => onChange({ ...data, activities_search_enabled: v })}
        disabled={disabled}
      />
      {[flightSearch, hotelSearch, activitiesSearch].filter(Boolean).length > 1 && (
        <Choice
          label="Search panel opens on"
          hint="Auto opens on Flights and reopens the tab a visitor last searched from. Pick a tab to pin the page to it (the /hotels page to Hotels, /activities to Activities)."
          value={typeof data.search_default_tab === 'string' ? data.search_default_tab : 'auto'}
          options={[
            { value: 'auto', label: 'Auto' },
            { value: 'flights', label: 'Flights' },
            { value: 'hotels', label: 'Hotels' },
            { value: 'activities', label: 'Activities' },
          ]}
          onChange={(v) => onChange({ ...data, search_default_tab: v })}
          disabled={disabled}
        />
      )}
      {[flightSearch, hotelSearch, activitiesSearch].filter(Boolean).length > 1 &&
        typeof data.search_default_tab === 'string' &&
        data.search_default_tab !== 'auto' && (
          <Toggle
            label="Other tabs open their own page"
            hint="On: picking Flights / Hotels / Activities here opens /flights, /hotels or /activities, so the whole page matches the tab. Off: the tab only swaps the search form, instantly. Turn it off if the page change feels slow."
            checked={data.search_tabs_navigate === true}
            onChange={(v) => onChange({ ...data, search_tabs_navigate: v })}
            disabled={disabled}
          />
        )}

      {(flightSearch || hotelSearch) && (
        <HeroSearchPrefill data={data} onChange={onChange} disabled={disabled} />
      )}

      {!panelSearch && (
        <>
          <Toggle
            label="Show search bar"
            hint="The “What are you looking for?” box in the hero."
            checked={searchEnabled}
            onChange={(v) => onChange({ ...data, search_enabled: v })}
            disabled={disabled}
          />
          {searchEnabled && (
            <Text
              label="Search placeholder"
              value={data.search_placeholder}
              onChange={(v) => onChange({ ...data, search_placeholder: v })}
              placeholder="What are you looking for ?"
              disabled={disabled}
            />
          )}
        </>
      )}
      {/* Guide hero only: the "Best time to visit" pages render these as meta
          chips under the headline. Other page types ignore them. */}
      <Repeater
        items={arr(data.meta_items)}
        onItems={(meta_items) => onChange({ ...data, meta_items })}
        blank={() => ({ text: '', icon: '' })}
        disabled={disabled}
        render={(item, set) => (
          <>
            <Text
              label="Meta chip text"
              value={item.text}
              onChange={(v) => set({ text: v })}
              placeholder="12 Min Read"
              disabled={disabled}
            />
            <IconPicker
              label="Icon"
              value={item.icon}
              onChange={(v) => set({ icon: v })}
              disabled={disabled}
            />
          </>
        )}
      />
      <p className="text-[12px] text-(--fg-faint)">
        Meta chips sit under the headline on “Best time to visit” guides (e.g. “Updated 2026”,
        “Weather Guide”, “12 Min Read”). Ignored on other page types.
      </p>

      <Toggle
        label="Show stats strip"
        hint="The trust/stats cards. Overlaps the hero bottom, or - when a search widget is on - sits under the “Stars Travelled With Us” section (straight under the hero if that section is hidden)."
        checked={statsEnabled}
        onChange={(v) => onChange({ ...data, stats_enabled: v })}
        disabled={disabled}
      />
      {statsEnabled && (
        <Repeater
          items={arr(data.stats)}
          onItems={(stats) => onChange({ ...data, stats })}
          blank={() => ({ value: '', label: '', icon: '' })}
          disabled={disabled}
          render={(item, set) => (
            <>
              <Text
                label="Value"
                value={item.value}
                onChange={(v) => set({ value: v })}
                placeholder="100K+"
                disabled={disabled}
              />
              <Text
                label="Label"
                value={item.label}
                onChange={(v) => set({ label: v })}
                placeholder="Happy Travelers"
                disabled={disabled}
              />
              <IconPicker
                label="Icon"
                value={item.icon}
                onChange={(v) => set({ icon: v })}
                disabled={disabled}
              />
            </>
          )}
        />
      )}
    </>
  );
};

const ServiceIconsRow = ({ data, onChange, disabled }: FormProps): React.JSX.Element => (
  <Repeater
    items={arr(data.items)}
    onItems={(items) => onChange({ ...data, items })}
    blank={() => ({ icon_media_id: '', label: '', href: '' })}
    disabled={disabled}
    render={(item, set) => (
      <>
        <MediaDropField
          variant="square"
          kind="image"
          value={item.icon_media_id as string}
          onChange={(id) => set({ icon_media_id: id })}
          disabled={disabled}
        />
        <Text
          label="Label"
          value={item.label}
          onChange={(v) => set({ label: v })}
          disabled={disabled}
        />
        <Text
          label="Link"
          value={item.href}
          onChange={(v) => set({ href: v })}
          disabled={disabled}
        />
      </>
    )}
  />
);

const Num = ({
  label,
  value,
  onChange,
  min,
  max,
  disabled,
}: {
  label: string;
  value: unknown;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  disabled?: boolean;
}): React.JSX.Element => (
  <div>
    <label className="mb-1 block text-xs font-medium text-(--fg-muted)">{label}</label>
    <UzInput
      type="number"
      min={min}
      max={max}
      value={typeof value === 'number' ? String(value) : ''}
      disabled={disabled}
      onChange={(e) => onChange(Number(e.target.value))}
    />
  </div>
);

const StarEndorsements = ({ data, onChange, disabled }: FormProps): React.JSX.Element => {
  const bool = (k: string, dflt = true): boolean =>
    data[k] === undefined ? dflt : data[k] === true;
  const num = (k: string, dflt: number): number =>
    typeof data[k] === 'number' ? (data[k] as number) : dflt;
  return (
    <>
      <HeadingHint />

      <Repeater
        items={arr(data.items)}
        onItems={(items) => onChange({ ...data, items })}
        blank={() => ({ name: '', blurb: '', photo_media_id: '', bg_colour: '#093F51' })}
        disabled={disabled}
        render={(item, set) => (
          <>
            <MediaDropField
              label="Photo (cut-out works best)"
              kind="image"
              value={item.photo_media_id as string}
              onChange={(id) => set({ photo_media_id: id })}
              disabled={disabled}
            />
            <Text
              label="Name"
              value={item.name}
              onChange={(v) => set({ name: v })}
              disabled={disabled}
            />
            <Area
              label="Role / blurb"
              value={item.blurb}
              onChange={(v) => set({ blurb: v })}
              disabled={disabled}
            />
            <Color
              label="Card colour"
              value={item.bg_colour}
              onChange={(v) => set({ bg_colour: v })}
              disabled={disabled}
            />
          </>
        )}
      />

      <div className="mt-1 text-xs font-semibold tracking-wide text-(--fg-muted) uppercase">
        Slider
      </div>
      <Toggle
        label="Autoplay"
        checked={bool('autoplay')}
        onChange={(v) => onChange({ ...data, autoplay: v })}
        disabled={disabled}
      />
      {bool('autoplay') && (
        <Num
          label="Autoplay interval (ms)"
          value={num('autoplay_ms', 5000)}
          min={1000}
          max={20000}
          onChange={(v) => onChange({ ...data, autoplay_ms: v })}
          disabled={disabled}
        />
      )}
      <Toggle
        label="Pause on hover"
        checked={bool('pause_on_hover')}
        onChange={(v) => onChange({ ...data, pause_on_hover: v })}
        disabled={disabled}
      />
      <Toggle
        label="Loop"
        checked={bool('loop')}
        onChange={(v) => onChange({ ...data, loop: v })}
        disabled={disabled}
      />
      <Toggle
        label="Show arrows"
        checked={bool('show_arrows')}
        onChange={(v) => onChange({ ...data, show_arrows: v })}
        disabled={disabled}
      />
      <Toggle
        label="Show dots"
        checked={bool('show_dots', false)}
        onChange={(v) => onChange({ ...data, show_dots: v })}
        disabled={disabled}
      />
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <Num
          label="Per view · desktop"
          value={num('per_view_desktop', 3)}
          min={1}
          max={6}
          onChange={(v) => onChange({ ...data, per_view_desktop: v })}
          disabled={disabled}
        />
        <Num
          label="Tablet"
          value={num('per_view_tablet', 2)}
          min={1}
          max={4}
          onChange={(v) => onChange({ ...data, per_view_tablet: v })}
          disabled={disabled}
        />
        <Num
          label="Mobile"
          value={num('per_view_mobile', 1)}
          min={1}
          max={2}
          onChange={(v) => onChange({ ...data, per_view_mobile: v })}
          disabled={disabled}
        />
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <Num
          label="Scroll · desktop"
          value={num('slides_to_scroll_desktop', 1)}
          min={1}
          max={5}
          onChange={(v) => onChange({ ...data, slides_to_scroll_desktop: v })}
          disabled={disabled}
        />
        <Num
          label="Tablet"
          value={num('slides_to_scroll_tablet', 1)}
          min={1}
          max={5}
          onChange={(v) => onChange({ ...data, slides_to_scroll_tablet: v })}
          disabled={disabled}
        />
        <Num
          label="Mobile"
          value={num('slides_to_scroll_mobile', 1)}
          min={1}
          max={5}
          onChange={(v) => onChange({ ...data, slides_to_scroll_mobile: v })}
          disabled={disabled}
        />
      </div>
    </>
  );
};

const DestinationShowcase = ({ data, onChange, disabled }: FormProps): React.JSX.Element => {
  const bool = (k: string, dflt = true): boolean =>
    data[k] === undefined ? dflt : data[k] === true;
  const num = (k: string, dflt: number): number =>
    typeof data[k] === 'number' ? (data[k] as number) : dflt;

  const categoryQuery = useCategoryPagesList({ page: 1, pageSize: 200, sortDir: 'asc' });
  const categoryOptions = (categoryQuery.data?.data ?? []).map((c) => ({
    value: c.id,
    label: c.title,
    description: `/${c.slug}`,
  }));
  const categoryId =
    typeof data.category_page_id === 'string' && data.category_page_id
      ? data.category_page_id
      : undefined;

  return (
    <>
      <HeadingHint />

      {/* Pick a category page; the showcase auto-lists all its destinations. */}
      <div className="rounded-md border border-(--border) bg-(--bg-subtle) p-3">
        <UzCombobox
          label="Category page"
          placeholder={categoryQuery.isLoading ? 'Loading categories…' : 'Select a category page'}
          searchPlaceholder="Search categories…"
          options={categoryOptions}
          value={categoryId}
          onValueChange={(id) => onChange({ ...data, category_page_id: id ?? null })}
          disabled={disabled || categoryQuery.isLoading}
          emptyText="No category pages"
        />
        <p className="mt-1.5 text-[12px] text-(--fg-faint)">
          {categoryId
            ? 'Every destination in this category is listed automatically (image, lowest price, package count). The heading uses the category hero heading; "View all" links to the category page, each destination to /category/destination.'
            : 'Select a category page to populate this showcase.'}
        </p>
      </div>

      <Text
        label="View-all label"
        value={data.view_all_label}
        onChange={(v) => onChange({ ...data, view_all_label: v })}
        placeholder="View all destinations"
        disabled={disabled}
      />
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Color
          label="Panel colour"
          value={data.panel_colour}
          onChange={(v) => onChange({ ...data, panel_colour: v })}
          disabled={disabled}
        />
        <Color
          label="Accent / pill colour"
          value={data.accent_colour}
          onChange={(v) => onChange({ ...data, accent_colour: v })}
          disabled={disabled}
        />
      </div>

      <div className="mt-1 text-xs font-semibold tracking-wide text-(--fg-muted) uppercase">
        Slider
      </div>
      <Toggle
        label="Autoplay"
        checked={bool('autoplay', false)}
        onChange={(v) => onChange({ ...data, autoplay: v })}
        disabled={disabled}
      />
      {bool('autoplay', false) && (
        <Num
          label="Autoplay interval (ms)"
          value={num('autoplay_ms', 6000)}
          min={1000}
          max={20000}
          onChange={(v) => onChange({ ...data, autoplay_ms: v })}
          disabled={disabled}
        />
      )}
      <Toggle
        label="Pause on hover"
        checked={bool('pause_on_hover')}
        onChange={(v) => onChange({ ...data, pause_on_hover: v })}
        disabled={disabled}
      />
      <Toggle
        label="Loop"
        checked={bool('loop')}
        onChange={(v) => onChange({ ...data, loop: v })}
        disabled={disabled}
      />
      <Toggle
        label="Show arrows"
        checked={bool('show_arrows')}
        onChange={(v) => onChange({ ...data, show_arrows: v })}
        disabled={disabled}
      />
      <Toggle
        label="Show dots"
        checked={bool('show_dots', false)}
        onChange={(v) => onChange({ ...data, show_dots: v })}
        disabled={disabled}
      />
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <Num
          label="Thumbs · desktop"
          value={num('per_view_desktop', 3)}
          min={1}
          max={5}
          onChange={(v) => onChange({ ...data, per_view_desktop: v })}
          disabled={disabled}
        />
        <Num
          label="Tablet"
          value={num('per_view_tablet', 2)}
          min={1}
          max={4}
          onChange={(v) => onChange({ ...data, per_view_tablet: v })}
          disabled={disabled}
        />
        <Num
          label="Mobile"
          value={num('per_view_mobile', 1)}
          min={1}
          max={2}
          onChange={(v) => onChange({ ...data, per_view_mobile: v })}
          disabled={disabled}
        />
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <Num
          label="Scroll · desktop"
          value={num('slides_to_scroll_desktop', 1)}
          min={1}
          max={5}
          onChange={(v) => onChange({ ...data, slides_to_scroll_desktop: v })}
          disabled={disabled}
        />
        <Num
          label="Tablet"
          value={num('slides_to_scroll_tablet', 1)}
          min={1}
          max={5}
          onChange={(v) => onChange({ ...data, slides_to_scroll_tablet: v })}
          disabled={disabled}
        />
        <Num
          label="Mobile"
          value={num('slides_to_scroll_mobile', 1)}
          min={1}
          max={5}
          onChange={(v) => onChange({ ...data, slides_to_scroll_mobile: v })}
          disabled={disabled}
        />
      </div>
    </>
  );
};

const CtaBanner = ({ data, onChange, disabled }: FormProps): React.JSX.Element => (
  <>
    <HeadingHint />
    <Text
      label="Date text"
      value={data.date_text}
      onChange={(v) => onChange({ ...data, date_text: v })}
      disabled={disabled}
    />
    <Text
      label="Price text"
      value={data.price_text}
      onChange={(v) => onChange({ ...data, price_text: v })}
      disabled={disabled}
    />
    <Text
      label="CTA link"
      value={data.cta_href}
      onChange={(v) => onChange({ ...data, cta_href: v })}
      disabled={disabled}
    />
    <Text
      label="CTA button text (guide pages only)"
      value={data.cta_text}
      onChange={(v) => onChange({ ...data, cta_text: v })}
      placeholder="Explore Packages"
      disabled={disabled}
    />
    <Color
      label="Background colour"
      value={data.bg_colour}
      onChange={(v) => onChange({ ...data, bg_colour: v })}
      disabled={disabled}
    />
    <p className="text-[12px] text-(--fg-faint)">
      On “Best time to visit” guides this section is pinned in the right rail as the packages promo
      (heading + supporting line + button), not as the full-width banner.
    </p>
  </>
);

const FeatureList = ({ data, onChange, disabled }: FormProps): React.JSX.Element => {
  // Two ordered sub-image slots stored in photo_media_ids (compacted - the
  // schema is an array of UUIDs, so empty slots aren't persisted).
  const photos = (data.photo_media_ids as string[] | undefined) ?? [];
  const setPhotoAt = (i: number, id: string | null): void => {
    const slots: (string | undefined)[] = [photos[0], photos[1]];
    slots[i] = id ?? undefined;
    onChange({ ...data, photo_media_ids: slots.filter((x): x is string => Boolean(x)) });
  };
  // "split" centres a single featured image with items running down both sides;
  // the two accent sub-images aren't used in that layout, so hide their slots.
  const isSplit = data.layout === 'split';
  return (
    <>
      <Text
        label="Eyebrow (guide pages only)"
        value={data.eyebrow}
        onChange={(v) => onChange({ ...data, eyebrow: v })}
        placeholder="Packing guide"
        disabled={disabled}
      />
      <Toggle
        label="Centered layout"
        hint="Featured image in the centre with items split on both sides (no sub-images)."
        checked={isSplit}
        onChange={(v) => onChange({ ...data, layout: v ? 'split' : 'default' })}
        disabled={disabled}
      />
      <MediaDropField
        label="Main illustration"
        kind="image"
        value={data.illustration_media_id as string}
        onChange={(id) => onChange({ ...data, illustration_media_id: id })}
        disabled={disabled}
      />
      {!isSplit && (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <MediaDropField
            label="Sub-image 1"
            kind="image"
            value={photos[0]}
            onChange={(id) => setPhotoAt(0, id)}
            disabled={disabled}
          />
          <MediaDropField
            label="Sub-image 2"
            kind="image"
            value={photos[1]}
            onChange={(id) => setPhotoAt(1, id)}
            disabled={disabled}
          />
        </div>
      )}
      <Repeater
        items={arr(data.items)}
        onItems={(items) => onChange({ ...data, items })}
        blank={() => ({ title: '', body: '' })}
        disabled={disabled}
        render={(item, set) => (
          <>
            {/* No icon field - the section renders auto-numbered 01/02/03 badges. */}
            <Text
              label="Title"
              value={item.title}
              onChange={(v) => set({ title: v })}
              disabled={disabled}
            />
            <Area
              label="Body"
              value={item.body}
              onChange={(v) => set({ body: v })}
              disabled={disabled}
            />
          </>
        )}
      />
    </>
  );
};

const ClientStories = ({ data, onChange, disabled }: FormProps): React.JSX.Element => {
  const bool = (k: string, dflt = true): boolean =>
    data[k] === undefined ? dflt : data[k] === true;
  const num = (k: string, dflt: number): number =>
    typeof data[k] === 'number' ? (data[k] as number) : dflt;
  return (
    <>
      <Toggle
        label="Compact spacing"
        hint="Drop the top padding so the cards sit flush under the section above (e.g. the photo gallery)."
        checked={data.compact === true}
        onChange={(v) => onChange({ ...data, compact: v })}
        disabled={disabled}
      />
      <Repeater
        items={arr(data.items)}
        onItems={(items) => onChange({ ...data, items })}
        blank={() => ({ photo_media_id: '', quote: '', rating: 5, traveller_name: '' })}
        disabled={disabled}
        render={(item, set) => (
          <>
            <MediaDropField
              label="Photo"
              kind="image"
              value={item.photo_media_id as string}
              onChange={(id) => set({ photo_media_id: id })}
              disabled={disabled}
            />
            <Area
              label="Quote"
              value={item.quote}
              onChange={(v) => set({ quote: v })}
              disabled={disabled}
            />
            <Text
              label="Traveller name"
              value={item.traveller_name}
              onChange={(v) => set({ traveller_name: v })}
              disabled={disabled}
            />
            <Text
              label="Rating (1-5)"
              value={String(item.rating ?? 5)}
              onChange={(v) => set({ rating: Number(v) || 5 })}
              disabled={disabled}
            />
          </>
        )}
      />

      <div className="mt-1 text-xs font-semibold tracking-wide text-(--fg-muted) uppercase">
        Slider
      </div>
      <Toggle
        label="Autoplay"
        checked={bool('autoplay', false)}
        onChange={(v) => onChange({ ...data, autoplay: v })}
        disabled={disabled}
      />
      {bool('autoplay', false) && (
        <Num
          label="Autoplay interval (ms)"
          value={num('autoplay_ms', 5000)}
          min={1000}
          max={20000}
          onChange={(v) => onChange({ ...data, autoplay_ms: v })}
          disabled={disabled}
        />
      )}
      <Toggle
        label="Pause on hover"
        checked={bool('pause_on_hover')}
        onChange={(v) => onChange({ ...data, pause_on_hover: v })}
        disabled={disabled}
      />
      <Toggle
        label="Loop"
        checked={bool('loop', false)}
        onChange={(v) => onChange({ ...data, loop: v })}
        disabled={disabled}
      />
      <Toggle
        label="Show arrows"
        checked={bool('show_arrows')}
        onChange={(v) => onChange({ ...data, show_arrows: v })}
        disabled={disabled}
      />
      <Toggle
        label="Show dots"
        checked={bool('show_dots', false)}
        onChange={(v) => onChange({ ...data, show_dots: v })}
        disabled={disabled}
      />
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <Num
          label="Per view · desktop"
          value={num('per_view_desktop', 3)}
          min={1}
          max={6}
          onChange={(v) => onChange({ ...data, per_view_desktop: v })}
          disabled={disabled}
        />
        <Num
          label="Tablet"
          value={num('per_view_tablet', 2)}
          min={1}
          max={4}
          onChange={(v) => onChange({ ...data, per_view_tablet: v })}
          disabled={disabled}
        />
        <Num
          label="Mobile"
          value={num('per_view_mobile', 1)}
          min={1}
          max={2}
          onChange={(v) => onChange({ ...data, per_view_mobile: v })}
          disabled={disabled}
        />
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <Num
          label="Scroll · desktop"
          value={num('slides_to_scroll_desktop', 1)}
          min={1}
          max={5}
          onChange={(v) => onChange({ ...data, slides_to_scroll_desktop: v })}
          disabled={disabled}
        />
        <Num
          label="Tablet"
          value={num('slides_to_scroll_tablet', 1)}
          min={1}
          max={5}
          onChange={(v) => onChange({ ...data, slides_to_scroll_tablet: v })}
          disabled={disabled}
        />
        <Num
          label="Mobile"
          value={num('slides_to_scroll_mobile', 1)}
          min={1}
          max={5}
          onChange={(v) => onChange({ ...data, slides_to_scroll_mobile: v })}
          disabled={disabled}
        />
      </div>
    </>
  );
};

const Reviews = ({ data, onChange, disabled }: FormProps): React.JSX.Element => {
  const bool = (k: string, dflt = true): boolean =>
    data[k] === undefined ? dflt : data[k] === true;
  const num = (k: string, dflt: number): number =>
    typeof data[k] === 'number' ? (data[k] as number) : dflt;
  return (
    <>
      <Toggle
        label="Compact spacing"
        hint="Drop the top padding so the cards sit flush under the section above."
        checked={data.compact === true}
        onChange={(v) => onChange({ ...data, compact: v })}
        disabled={disabled}
      />
      <Text
        label='Link label (e.g. "View details")'
        value={(data.link_label as string) ?? 'View details'}
        onChange={(v) => onChange({ ...data, link_label: v })}
        disabled={disabled}
      />
      <Repeater
        items={arr(data.items)}
        onItems={(items) => onChange({ ...data, items })}
        blank={() => ({ photo_media_id: '', quote: '', rating: 5, reviewer_name: '', href: '' })}
        disabled={disabled}
        render={(item, set) => (
          <>
            <MediaDropField
              label="Photo"
              kind="image"
              value={item.photo_media_id as string}
              onChange={(id) => set({ photo_media_id: id })}
              disabled={disabled}
            />
            <Area
              label="Quote"
              value={item.quote}
              onChange={(v) => set({ quote: v })}
              disabled={disabled}
            />
            <Text
              label="Reviewer name"
              value={item.reviewer_name}
              onChange={(v) => set({ reviewer_name: v })}
              disabled={disabled}
            />
            <Text
              label="Rating (1-5)"
              value={String(item.rating ?? 5)}
              onChange={(v) => set({ rating: Number(v) || 5 })}
              disabled={disabled}
            />
            <Text
              label="View details link - URL (optional, empty = hidden)"
              value={item.href}
              onChange={(v) => set({ href: v })}
              disabled={disabled}
            />
          </>
        )}
      />

      <div className="mt-1 text-xs font-semibold tracking-wide text-(--fg-muted) uppercase">
        Slider
      </div>
      <Toggle
        label="Autoplay"
        checked={bool('autoplay', false)}
        onChange={(v) => onChange({ ...data, autoplay: v })}
        disabled={disabled}
      />
      {bool('autoplay', false) && (
        <Num
          label="Autoplay interval (ms)"
          value={num('autoplay_ms', 5000)}
          min={1000}
          max={20000}
          onChange={(v) => onChange({ ...data, autoplay_ms: v })}
          disabled={disabled}
        />
      )}
      <Toggle
        label="Pause on hover"
        checked={bool('pause_on_hover')}
        onChange={(v) => onChange({ ...data, pause_on_hover: v })}
        disabled={disabled}
      />
      <Toggle
        label="Loop"
        checked={bool('loop', false)}
        onChange={(v) => onChange({ ...data, loop: v })}
        disabled={disabled}
      />
      <Toggle
        label="Show arrows"
        checked={bool('show_arrows')}
        onChange={(v) => onChange({ ...data, show_arrows: v })}
        disabled={disabled}
      />
      <Toggle
        label="Show dots"
        checked={bool('show_dots', false)}
        onChange={(v) => onChange({ ...data, show_dots: v })}
        disabled={disabled}
      />
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <Num
          label="Per view · desktop"
          value={num('per_view_desktop', 3)}
          min={1}
          max={6}
          onChange={(v) => onChange({ ...data, per_view_desktop: v })}
          disabled={disabled}
        />
        <Num
          label="Tablet"
          value={num('per_view_tablet', 2)}
          min={1}
          max={4}
          onChange={(v) => onChange({ ...data, per_view_tablet: v })}
          disabled={disabled}
        />
        <Num
          label="Mobile"
          value={num('per_view_mobile', 1)}
          min={1}
          max={2}
          onChange={(v) => onChange({ ...data, per_view_mobile: v })}
          disabled={disabled}
        />
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <Num
          label="Scroll · desktop"
          value={num('slides_to_scroll_desktop', 1)}
          min={1}
          max={5}
          onChange={(v) => onChange({ ...data, slides_to_scroll_desktop: v })}
          disabled={disabled}
        />
        <Num
          label="Tablet"
          value={num('slides_to_scroll_tablet', 1)}
          min={1}
          max={5}
          onChange={(v) => onChange({ ...data, slides_to_scroll_tablet: v })}
          disabled={disabled}
        />
        <Num
          label="Mobile"
          value={num('slides_to_scroll_mobile', 1)}
          min={1}
          max={5}
          onChange={(v) => onChange({ ...data, slides_to_scroll_mobile: v })}
          disabled={disabled}
        />
      </div>
    </>
  );
};

const AwardsBlock = ({ data, onChange, disabled }: FormProps): React.JSX.Element => {
  const isCards = data.variant === 'cards';
  return (
    <>
      <HeadingHint />
      <Choice
        label="Style"
        hint="Timeline = medallions along a connector line (homepage) · Cards = stacked cards with a photo of each award (Our Awards page)"
        value={isCards ? 'cards' : 'timeline'}
        options={[
          { value: 'timeline', label: 'Timeline' },
          { value: 'cards', label: 'Cards' },
        ]}
        onChange={(v) => onChange({ ...data, variant: v })}
        disabled={disabled}
      />
      <Repeater
        items={arr(data.items)}
        onItems={(items) => onChange({ ...data, items })}
        blank={() => ({ title: '', body: '', icon: '', image_media_id: '' })}
        disabled={disabled}
        render={(item, set) => (
          <>
            {isCards && (
              <MediaDropField
                label="Award photo"
                kind="image"
                value={item.image_media_id as string}
                onChange={(id) => set({ image_media_id: id })}
                disabled={disabled}
              />
            )}
            <IconPicker
              label={isCards ? 'Icon (fallback when no photo)' : 'Icon'}
              value={item.icon}
              onChange={(v) => set({ icon: v })}
              disabled={disabled}
            />
            <Text
              label="Title"
              value={item.title}
              onChange={(v) => set({ title: v })}
              disabled={disabled}
            />
            <Area
              label="Body"
              value={item.body}
              onChange={(v) => set({ body: v })}
              disabled={disabled}
            />
          </>
        )}
      />
      <Text
        label="CTA link (optional)"
        value={data.cta_href}
        onChange={(v) => onChange({ ...data, cta_href: v })}
        disabled={disabled}
      />
    </>
  );
};

const CallbackForm = ({ data, onChange, disabled }: FormProps): React.JSX.Element => (
  <>
    <MediaDropField
      label="Side image"
      kind="image"
      value={data.image_media_id as string}
      onChange={(id) => onChange({ ...data, image_media_id: id })}
      disabled={disabled}
    />
    <HeadingHint />
    <Text
      label="Query source code"
      value={data.query_source_code}
      onChange={(v) => onChange({ ...data, query_source_code: v })}
      placeholder="website_callback_form"
      disabled={disabled}
    />
  </>
);

const MiceForm = ({ data, onChange, disabled }: FormProps): React.JSX.Element => (
  <>
    <HeadingHint />
    <Text
      label="Query source code"
      value={data.query_source_code}
      onChange={(v) => onChange({ ...data, query_source_code: v })}
      placeholder="website-mice"
      disabled={disabled}
    />
  </>
);

const FaqList = ({ data, onChange, disabled }: FormProps): React.JSX.Element => (
  <>
    <HeadingHint />
    <Repeater
      items={arr(data.items)}
      onItems={(items) => onChange({ ...data, items })}
      blank={() => ({ question: '', answer: '' })}
      disabled={disabled}
      render={(item, set) => (
        <>
          <Text
            label="Question"
            value={item.question}
            onChange={(v) => set({ question: v })}
            disabled={disabled}
          />
          <Area
            label="Answer"
            value={item.answer}
            onChange={(v) => set({ answer: v })}
            disabled={disabled}
          />
        </>
      )}
    />
  </>
);

/**
 * LOCATIONS: offices are picked from the Branch master (Masters → Branches) by
 * id - just a dropdown, for accuracy. The card text (name, badge, address,
 * phone, email) and the exact Google Maps pin all live on the branch record, so
 * there's nothing to type here. Selected branches render as a reorderable list.
 */
const Locations = ({ data, onChange, disabled }: FormProps): React.JSX.Element => {
  const branchIds = Array.isArray(data.branch_ids) ? (data.branch_ids as string[]) : [];
  const branchesQuery = useBranchesList({
    page: 1,
    pageSize: 200,
    isActive: true,
    sortBy: 'sortOrder',
    sortDir: 'asc',
  });
  const branches = branchesQuery.data?.data ?? [];
  const byId = new Map(branches.map((b) => [b.id, b]));
  const available = branches
    .filter((b) => !branchIds.includes(b.id))
    .map((b) => ({ value: b.id, label: b.name, description: b.officeBadge ?? undefined }));

  const setIds = (next: string[]): void => onChange({ ...data, branch_ids: next });
  const move = (i: number, to: number): void => {
    const next = [...branchIds];
    [next[i], next[to]] = [next[to]!, next[i]!];
    setIds(next);
  };

  return (
    <>
      <HeadingHint />
      <UzCombobox
        label="Add an office"
        placeholder={branchesQuery.isLoading ? 'Loading branches…' : 'Select a branch to add'}
        searchPlaceholder="Search branches…"
        options={available}
        value={undefined}
        onValueChange={(id) => id && setIds([...branchIds, id])}
        disabled={disabled || branchesQuery.isLoading}
        emptyText="No more branches"
      />
      {branchIds.length === 0 ? (
        <p className="text-[12px] text-(--fg-faint)">
          No offices selected. Offices come from <strong>Masters → Branches</strong> - pick which to
          show. Set each branch&apos;s address, badge and Google Maps embed there.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {branchIds.map((id, i) => {
            const b = byId.get(id);
            return (
              <div
                key={id}
                className="flex items-center justify-between gap-2 rounded-md border border-(--border) bg-(--z-50) px-3 py-2"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-(--fg)">
                    {b ? b.name : 'Unknown branch'}
                    {b?.officeBadge ? (
                      <span className="ml-1 font-normal text-(--fg-faint)">({b.officeBadge})</span>
                    ) : null}
                  </div>
                  {!b && (
                    <div className="text-[12px] text-(--ds-error)">
                      This branch no longer exists - remove it.
                    </div>
                  )}
                </div>
                {!disabled && (
                  <div className="flex items-center gap-2 text-(--fg-faint)">
                    <button
                      type="button"
                      disabled={i === 0}
                      onClick={() => move(i, i - 1)}
                      className="hover:text-(--fg) disabled:cursor-not-allowed disabled:opacity-30"
                      aria-label="Move up"
                      title="Move up"
                    >
                      <ChevronUp className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      disabled={i === branchIds.length - 1}
                      onClick={() => move(i, i + 1)}
                      className="hover:text-(--fg) disabled:cursor-not-allowed disabled:opacity-30"
                      aria-label="Move down"
                      title="Move down"
                    >
                      <ChevronDown className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setIds(branchIds.filter((x) => x !== id))}
                      className="hover:text-(--ds-error)"
                      aria-label="Remove"
                      title="Remove"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

const FeaturedCategory = ({ data, onChange, disabled }: FormProps): React.JSX.Element => {
  const featured = (data.featured as Data) ?? {};
  const setFeatured = (patch: Data): void =>
    onChange({ ...data, featured: { ...featured, ...patch } });
  return (
    <>
      <Text
        label="Category label"
        value={data.category_label}
        onChange={(v) => onChange({ ...data, category_label: v })}
        disabled={disabled}
      />
      <Text
        label="Featured destination id"
        value={featured.destination_id}
        onChange={(v) => setFeatured({ destination_id: v })}
        disabled={disabled}
      />
      <Text
        label="Price text"
        value={featured.price_text}
        onChange={(v) => setFeatured({ price_text: v })}
        disabled={disabled}
      />
      <Text
        label="CTA text"
        value={featured.cta_text}
        onChange={(v) => setFeatured({ cta_text: v })}
        disabled={disabled}
      />
      <Text
        label="CTA link"
        value={featured.cta_href}
        onChange={(v) => setFeatured({ cta_href: v })}
        disabled={disabled}
      />
      <p className="text-[12px] text-(--fg-faint)">
        Tiles + a destination picker land with the catalog screens.
      </p>
    </>
  );
};

/**
 * Editor's Picks: the content is now resolved automatically from each blog
 * post's "Show in Editor's Picks" toggle (most recently featured fills the large
 * slot, then up to four more), so there's nothing to curate here - only the
 * section's heading / subheading (edited above). This panel just explains that.
 */
const EditorsPicks = (_props: FormProps): React.JSX.Element => (
  <>
    <HeadingHint />
    <div className="rounded-md border border-(--border) bg-(--z-50) p-3 text-xs text-(--fg-muted)">
      <p className="font-semibold text-(--fg)">Managed automatically</p>
      <p className="mt-1 leading-relaxed">
        Posts shown here are chosen by the <strong>“Show in Editor&apos;s Picks”</strong> toggle on
        each blog post - the most recently featured post fills the large slot, followed by up to
        four more. Turn the toggle on from <em>Blogs → edit a post</em>.
      </p>
    </div>
  </>
);

const Gallery = ({ data, onChange, disabled }: FormProps): React.JSX.Element => {
  const ids = (data.photo_media_ids as string[] | undefined) ?? [];
  const cols = Number(data.columns ?? 7);
  return (
    <>
      <p className="text-[12px] text-(--fg-faint)">
        Heading &amp; subheading come from this section&apos;s title / subtitle fields above.
      </p>
      <MediaGalleryField
        label="Photos"
        value={ids}
        onChange={(next) => onChange({ ...data, photo_media_ids: next })}
        disabled={disabled}
      />
      <Text
        label="Columns (desktop, 3-8)"
        value={String(cols)}
        onChange={(v) => onChange({ ...data, columns: Math.min(8, Math.max(3, Number(v) || 7)) })}
        disabled={disabled}
      />
      <Toggle
        label="Floating motion"
        hint="Adjacent columns drift gently up/down in a permanent, calm loop."
        checked={data.animate !== false}
        onChange={(v) => onChange({ ...data, animate: v })}
        disabled={disabled}
      />
    </>
  );
};

/**
 * Reusable slider-controls block for the logo-strip sections (Partners, Press).
 * Mirrors the StarEndorsements pattern minus dots/arrows (logos don't need them
 * for a marquee feel) and adds the `continuous_mode` toggle that switches the
 * row(s) from stepped autoplay to a smooth marquee scroll driven by the same
 * autoplay interval.
 */
const LogoSliderControls = ({ data, onChange, disabled }: FormProps): React.JSX.Element => {
  const bool = (k: string, dflt = true): boolean =>
    data[k] === undefined ? dflt : data[k] === true;
  const num = (k: string, dflt: number): number =>
    typeof data[k] === 'number' ? (data[k] as number) : dflt;
  return (
    <>
      <div className="mt-1 text-xs font-semibold tracking-wide text-(--fg-muted) uppercase">
        Slider
      </div>
      <Toggle
        label="Autoplay"
        checked={bool('autoplay')}
        onChange={(v) => onChange({ ...data, autoplay: v })}
        disabled={disabled}
      />
      {bool('autoplay') && (
        <>
          <Num
            label="Autoplay interval (ms)"
            value={num('autoplay_ms', 4000)}
            min={1000}
            max={20000}
            onChange={(v) => onChange({ ...data, autoplay_ms: v })}
            disabled={disabled}
          />
          <Toggle
            label="Continuous mode (smooth marquee scroll)"
            hint="When ON, rows scroll smoothly without stepped pauses. The interval above drives the speed (smaller = faster). When OFF, rows step every interval."
            checked={bool('continuous_mode', false)}
            onChange={(v) => onChange({ ...data, continuous_mode: v })}
            disabled={disabled}
          />
        </>
      )}
      <Toggle
        label="Pause on hover"
        checked={bool('pause_on_hover')}
        onChange={(v) => onChange({ ...data, pause_on_hover: v })}
        disabled={disabled}
      />
      <Toggle
        label="Loop"
        checked={bool('loop')}
        onChange={(v) => onChange({ ...data, loop: v })}
        disabled={disabled}
      />
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <Num
          label="Per view · desktop"
          value={num('per_view_desktop', 4)}
          min={2}
          max={8}
          onChange={(v) => onChange({ ...data, per_view_desktop: v })}
          disabled={disabled}
        />
        <Num
          label="Tablet"
          value={num('per_view_tablet', 3)}
          min={2}
          max={6}
          onChange={(v) => onChange({ ...data, per_view_tablet: v })}
          disabled={disabled}
        />
        <Num
          label="Mobile"
          value={num('per_view_mobile', 2)}
          min={1}
          max={4}
          onChange={(v) => onChange({ ...data, per_view_mobile: v })}
          disabled={disabled}
        />
      </div>
    </>
  );
};

const PartnersLogosForm = ({ data, onChange, disabled }: FormProps): React.JSX.Element => {
  const ids = (data.media_ids as string[] | undefined) ?? [];
  return (
    <>
      <HeadingHint />
      <MediaGalleryField
        label="Partner logos"
        value={ids}
        onChange={(next) => onChange({ ...data, media_ids: next })}
        disabled={disabled}
      />
      <p className="text-[12px] text-(--fg-faint)">
        {data.single_row === true
          ? 'Renders as ONE row of logos. With autoplay on, the logos repeat so the strip keeps scrolling even with only a few of them. Drag thumbnails to reorder.'
          : 'Renders as two rows scrolling in opposite directions (row 1 left→right, row 2 right→left). Items are split roughly in half across the rows. Drag thumbnails to reorder.'}
      </p>
      <Toggle
        label="Single row"
        hint="Show every logo in one row (e.g. 5 partner logos side by side) instead of two counter-scrolling rows."
        checked={data.single_row === true}
        onChange={(v) => onChange({ ...data, single_row: v })}
        disabled={disabled}
      />
      <LogoSliderControls data={data} onChange={onChange} disabled={disabled} />
    </>
  );
};

const MiceAccreditationsForm = ({ data, onChange, disabled }: FormProps): React.JSX.Element => {
  const ids = (data.media_ids as string[] | undefined) ?? [];
  return (
    <>
      <HeadingHint />
      <MediaGalleryField
        label="Accreditation / partner logos"
        value={ids}
        onChange={(next) => onChange({ ...data, media_ids: next })}
        disabled={disabled}
      />
      <p className="text-[12px] text-(--fg-faint)">
        Logos render as a static grid (4 per row on desktop). Drag thumbnails to reorder.
      </p>
    </>
  );
};

const MicePartnersMarqueeForm = ({ data, onChange, disabled }: FormProps): React.JSX.Element => {
  const ids = (data.media_ids as string[] | undefined) ?? [];
  return (
    <>
      <HeadingHint />
      <MediaGalleryField
        label="Partner / client logos"
        value={ids}
        onChange={(next) => onChange({ ...data, media_ids: next })}
        disabled={disabled}
      />
      <p className="text-[12px] text-(--fg-faint)">
        Renders as a single scrolling row (marquee). Drag thumbnails to reorder.
      </p>
      <LogoSliderControls data={data} onChange={onChange} disabled={disabled} />
    </>
  );
};

/**
 * MICE_HERO: "Corporate Travel Made Effortless" hero. The hero heading + subhead
 * come from the section's title / subtitle fields; pick a background image, the
 * right-hand lead form's heading + button label + lead source code, and an
 * optional stats strip (value + label) shown under the copy.
 */
const MiceHeroForm = ({ data, onChange, disabled }: FormProps): React.JSX.Element => (
  <>
    <MediaDropField
      label="Background image"
      kind="image"
      value={data.image_media_id as string}
      onChange={(id) => onChange({ ...data, image_media_id: id })}
      disabled={disabled}
    />
    <HeadingHint />
    <Text
      label="Form heading"
      value={data.form_title}
      onChange={(v) => onChange({ ...data, form_title: v })}
      placeholder="Plan Your Corporate Offsite"
      disabled={disabled}
    />
    <Text
      label="Form button label"
      value={data.form_cta_text}
      onChange={(v) => onChange({ ...data, form_cta_text: v })}
      placeholder="Get Proposal"
      disabled={disabled}
    />
    <Text
      label="Query source code"
      value={data.query_source_code}
      onChange={(v) => onChange({ ...data, query_source_code: v })}
      placeholder="website-mice"
      disabled={disabled}
    />
    <div className="mt-1 text-xs font-semibold tracking-wide text-(--fg-muted) uppercase">
      Stats strip
    </div>
    <Repeater
      items={arr(data.stats)}
      onItems={(stats) => onChange({ ...data, stats })}
      blank={() => ({ value: '', label: '' })}
      disabled={disabled}
      render={(item, set) => (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <Text
            label="Value"
            value={item.value}
            onChange={(v) => set({ value: v })}
            placeholder="100K+"
            disabled={disabled}
          />
          <Text
            label="Label"
            value={item.label}
            onChange={(v) => set({ label: v })}
            placeholder="Happy Travellers"
            disabled={disabled}
          />
        </div>
      )}
    />
  </>
);

/**
 * MICE_SERVICES: "Our MICE Services" four-pillar grid. Heading + supporting line
 * come from the section's title / subtitle fields; each pillar is an icon, a
 * title, a short description and a bullet list of inclusions (one per line).
 */
const MiceServicesForm = ({ data, onChange, disabled }: FormProps): React.JSX.Element => (
  <>
    <HeadingHint />
    <Num
      label="Pillars per row (desktop, 2-4)"
      value={typeof data.columns === 'number' ? (data.columns as number) : 4}
      min={2}
      max={4}
      onChange={(v) => onChange({ ...data, columns: v })}
      disabled={disabled}
    />
    <Repeater
      items={arr(data.items)}
      onItems={(items) => onChange({ ...data, items })}
      blank={() => ({ icon_media_id: '', title: '', description: '', bullets: [] })}
      disabled={disabled}
      render={(item, set) => (
        <>
          <MediaDropField
            label="Icon"
            kind="image"
            value={item.icon_media_id as string}
            onChange={(id) => set({ icon_media_id: id })}
            disabled={disabled}
          />
          <Text
            label="Title"
            value={item.title}
            onChange={(v) => set({ title: v })}
            placeholder="Incentive Travel Programs"
            disabled={disabled}
          />
          <Area
            label="Description"
            value={item.description}
            onChange={(v) => set({ description: v })}
            disabled={disabled}
          />
          <Area
            label="Bullets (one per line)"
            value={linesToText(item.bullets)}
            onChange={(v) => set({ bullets: textToLines(v) })}
            disabled={disabled}
          />
        </>
      )}
    />
  </>
);

/**
 * MICE_CASE_STUDIES: "Trusted By Leading Brands" success-story cards. Heading +
 * subhead come from the section's title / subtitle fields; each card is a
 * category/region eyebrow, people/location stats, a title + intro, a "what we
 * delivered" bullet list (one bullet per line), one hero image and supporting
 * thumbnails. Add as many cards as you like - they stack vertically.
 */
const MiceCaseStudiesForm = ({ data, onChange, disabled }: FormProps): React.JSX.Element => (
  <>
    <HeadingHint />
    <Repeater
      items={arr(data.items)}
      onItems={(items) => onChange({ ...data, items })}
      blank={() => ({
        logo_media_id: '',
        category: '',
        region: '',
        title: '',
        description: '',
        deliverables: [],
        people: '',
        location: '',
        image_media_id: '',
        gallery_media_ids: [],
      })}
      disabled={disabled}
      render={(item, set) => (
        <>
          <MediaDropField
            label="Client logo"
            kind="image"
            value={item.logo_media_id as string}
            onChange={(id) => set({ logo_media_id: id })}
            disabled={disabled}
          />
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Text
              label="Category"
              value={item.category}
              onChange={(v) => set({ category: v })}
              placeholder="Mobility & Transport"
              disabled={disabled}
            />
            <Text
              label="Region / scope"
              value={item.region}
              onChange={(v) => set({ region: v })}
              placeholder="Pan-India"
              disabled={disabled}
            />
          </div>
          <Text
            label="Title"
            value={item.title}
            onChange={(v) => set({ title: v })}
            placeholder="Pan-India Hotel Management for TCS iON"
            disabled={disabled}
          />
          <Area
            label="Description"
            value={item.description}
            onChange={(v) => set({ description: v })}
            disabled={disabled}
          />
          <Area
            label="What we delivered (one bullet per line)"
            value={linesToText(item.deliverables)}
            onChange={(v) => set({ deliverables: textToLines(v) })}
            disabled={disabled}
          />
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Text
              label="No. of People"
              value={item.people}
              onChange={(v) => set({ people: v })}
              placeholder="100+ People"
              disabled={disabled}
            />
            <Text
              label="Location"
              value={item.location}
              onChange={(v) => set({ location: v })}
              placeholder="India"
              disabled={disabled}
            />
          </div>
          <MediaDropField
            label="Hero image"
            kind="image"
            value={item.image_media_id as string}
            onChange={(id) => set({ image_media_id: id })}
            disabled={disabled}
          />
          <MediaGalleryField
            label="Supporting thumbnails"
            value={(item.gallery_media_ids as string[] | undefined) ?? []}
            onChange={(next) => set({ gallery_media_ids: next })}
            disabled={disabled}
          />
        </>
      )}
    />
  </>
);

/**
 * MICE_DESTINATIONS: "Destinations We Cover". Heading + subhead come from the
 * section's title / subtitle fields; each group is a coloured band with a
 * heading, subheading, per-card eyebrow, and an ordered multi-select of
 * Destination master rows (with a Domestic / International scope filter)
 * shown as a carousel of cards. Add as many groups as you like - they stack
 * vertically.
 */
const MiceDestinationsForm = ({ data, onChange, disabled }: FormProps): React.JSX.Element => (
  <>
    <HeadingHint />
    <Repeater
      items={arr(data.groups)}
      onItems={(groups) => onChange({ ...data, groups })}
      blank={() => ({
        heading: '',
        subheading: '',
        card_eyebrow: '',
        accent_colour: '',
        destination_ids: [],
      })}
      disabled={disabled}
      render={(item, set) => (
        <>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Text
              label="Group heading"
              value={item.heading}
              onChange={(v) => set({ heading: v })}
              placeholder="Domestic MICE"
              disabled={disabled}
            />
            <Text
              label="Group subheading"
              value={item.subheading}
              onChange={(v) => set({ subheading: v })}
              placeholder="Pan-India Coverages"
              disabled={disabled}
            />
          </div>
          <Text
            label="Card eyebrow (shown on every card)"
            value={item.card_eyebrow}
            onChange={(v) => set({ card_eyebrow: v })}
            placeholder="PAN-INDIA COVERAGES"
            disabled={disabled}
          />
          <Color
            label="Band colour"
            value={item.accent_colour}
            onChange={(v) => set({ accent_colour: v })}
            disabled={disabled}
          />
          <DestinationMultiSelect
            label="Destinations (in display order)"
            miceOnly
            value={
              Array.isArray(item.destination_ids)
                ? (item.destination_ids as unknown[]).filter(
                    (v): v is string => typeof v === 'string',
                  )
                : []
            }
            onChange={(ids) => set({ destination_ids: ids })}
            disabled={disabled}
          />
        </>
      )}
    />
  </>
);

/**
 * CLIENT_STORY_CARDS: image-rich testimonial carousel. Heading + subhead come
 * from the section's title / subtitle fields; each card has a hero image, up to
 * three thumbnails, an avatar, a name + date, a 0-5 rating and a quote. Add as
 * many cards as you like - they scroll horizontally.
 */
const ClientStoryCardsForm = ({ data, onChange, disabled }: FormProps): React.JSX.Element => (
  <>
    <HeadingHint />
    <Repeater
      items={arr(data.items)}
      onItems={(items) => onChange({ ...data, items })}
      blank={() => ({
        image_media_id: '',
        gallery_media_ids: [],
        photo_media_id: '',
        traveller_name: '',
        date_label: '',
        rating: 5,
        quote: '',
      })}
      disabled={disabled}
      render={(item, set) => (
        <>
          <MediaDropField
            label="Hero image"
            kind="image"
            value={item.image_media_id as string}
            onChange={(id) => set({ image_media_id: id })}
            disabled={disabled}
          />
          <MediaGalleryField
            label="Thumbnails (up to 3)"
            value={(item.gallery_media_ids as string[] | undefined) ?? []}
            onChange={(next) => set({ gallery_media_ids: next.slice(0, 3) })}
            disabled={disabled}
          />
          <MediaDropField
            label="Traveller avatar"
            kind="image"
            value={item.photo_media_id as string}
            onChange={(id) => set({ photo_media_id: id })}
            disabled={disabled}
          />
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Text
              label="Traveller name"
              value={item.traveller_name}
              onChange={(v) => set({ traveller_name: v })}
              placeholder="Pardeep Kaur"
              disabled={disabled}
            />
            <Text
              label="Date label"
              value={item.date_label}
              onChange={(v) => set({ date_label: v })}
              placeholder="12 May, 2024"
              disabled={disabled}
            />
          </div>
          <Text
            label="Rating (0-5)"
            value={typeof item.rating === 'number' ? String(item.rating) : '5'}
            onChange={(v) => {
              const n = Number(v);
              set({ rating: v === '' ? 5 : Math.max(0, Math.min(5, Number.isNaN(n) ? 5 : n)) });
            }}
            placeholder="5"
            disabled={disabled}
          />
          <Area
            label="Quote"
            value={item.quote}
            onChange={(v) => set({ quote: v })}
            disabled={disabled}
          />
        </>
      )}
    />
  </>
);

const PressLogosForm = ({ data, onChange, disabled }: FormProps): React.JSX.Element => {
  const ids = (data.media_ids as string[] | undefined) ?? [];
  return (
    <>
      <HeadingHint />
      <MediaGalleryField
        label="Press logos"
        value={ids}
        onChange={(next) => onChange({ ...data, media_ids: next })}
        disabled={disabled}
      />
      <p className="text-[12px] text-(--fg-faint)">Drag thumbnails to reorder.</p>
      <LogoSliderControls data={data} onChange={onChange} disabled={disabled} />
    </>
  );
};

const ExploreStories = ({ data, onChange, disabled }: FormProps): React.JSX.Element => {
  const bool = (k: string, dflt: boolean): boolean =>
    data[k] === undefined ? dflt : data[k] === true;
  const num = (k: string, dflt: number): number =>
    typeof data[k] === 'number' ? (data[k] as number) : dflt;
  const set = (patch: Data): void => onChange({ ...data, ...patch });
  return (
    <>
      <HeadingHint />
      <Toggle
        label="Destination filter"
        hint="Show the destination filter group in the rail."
        checked={bool('show_destination_filter', true)}
        onChange={(v) => set({ show_destination_filter: v })}
        disabled={disabled}
      />
      <Toggle
        label="Category filter"
        hint="Show the category (Browse by Interest) filter group."
        checked={bool('show_interest_filter', true)}
        onChange={(v) => set({ show_interest_filter: v })}
        disabled={disabled}
      />
      <Toggle
        label="Theme filter"
        hint="Show the theme (Browse by Theme) filter group."
        checked={bool('show_theme_filter', true)}
        onChange={(v) => set({ show_theme_filter: v })}
        disabled={disabled}
      />
      <Toggle
        label="Search box"
        hint="Show the search field above the grid."
        checked={bool('search_enabled', true)}
        onChange={(v) => set({ search_enabled: v })}
        disabled={disabled}
      />
      <Toggle
        label="Oldest first"
        hint="Order the grid oldest → newest instead of newest → oldest."
        checked={(typeof data.sort_order === 'string' ? data.sort_order : 'newest') === 'oldest'}
        onChange={(v) => set({ sort_order: v ? 'oldest' : 'newest' })}
        disabled={disabled}
      />
      <Num
        label="Cards per page (Load more batch)"
        value={num('page_size', 12)}
        onChange={(v) => set({ page_size: v })}
        min={3}
        max={48}
        disabled={disabled}
      />
    </>
  );
};

/**
 * TEXT_BLOCK: a reusable heading + description block (e.g. the about page's
 * "Our Story"). Heading / supporting line come from the section's title /
 * subtitle fields; the long-form description lives in `body` (split into
 * paragraphs on blank lines at render). An optional banner image renders above.
 */
const TextBlock = ({ data, onChange, disabled }: FormProps): React.JSX.Element => (
  <>
    <MediaDropField
      label="Banner image (optional)"
      kind="image"
      value={data.image_media_id as string}
      onChange={(id) => onChange({ ...data, image_media_id: id })}
      disabled={disabled}
    />
    <HeadingHint />
    <Text
      label="Eyebrow (optional)"
      value={data.eyebrow}
      onChange={(v) => onChange({ ...data, eyebrow: v })}
      placeholder="Small label above the heading"
      disabled={disabled}
    />
    <Area
      label="Description (separate paragraphs with a blank line)"
      value={data.body}
      onChange={(v) => onChange({ ...data, body: v })}
      disabled={disabled}
    />
    <Choice
      label="Style"
      hint="Plain = text on the page · Card = filled navy callout box (ignores the banner image)"
      value={data.variant === 'card' ? 'card' : 'plain'}
      options={[
        { value: 'plain', label: 'Plain' },
        { value: 'card', label: 'Card' },
      ]}
      onChange={(v) => onChange({ ...data, variant: v })}
      disabled={disabled}
    />
    <Choice
      label="Alignment"
      value={data.align === 'center' ? 'center' : 'left'}
      options={[
        { value: 'left', label: 'Left' },
        { value: 'center', label: 'Center' },
      ]}
      onChange={(v) => onChange({ ...data, align: v })}
      disabled={disabled}
    />
    <Text
      label="Button text (optional)"
      value={data.cta_text}
      onChange={(v) => onChange({ ...data, cta_text: v })}
      placeholder="View Open Positions"
      disabled={disabled}
    />
    <Text
      label="Button link (optional)"
      value={data.cta_href}
      onChange={(v) => onChange({ ...data, cta_href: v })}
      placeholder="#open-positions or /career"
      disabled={disabled}
    />
  </>
);

/**
 * TEAM_PROFILES: founder / leadership / team cards. Heading + supporting line
 * come from the section's title / subtitle fields; each card is a photo + name
 * + role + bio. Cards render photo-left on the website.
 */
const TeamProfiles = ({ data, onChange, disabled }: FormProps): React.JSX.Element => (
  <>
    <HeadingHint />
    <Repeater
      items={arr(data.items)}
      onItems={(items) => onChange({ ...data, items })}
      blank={() => ({ photo_media_id: '', name: '', role: '', bio: '' })}
      disabled={disabled}
      render={(item, set) => (
        <>
          <MediaDropField
            label="Photo"
            kind="image"
            value={item.photo_media_id as string}
            onChange={(id) => set({ photo_media_id: id })}
            disabled={disabled}
          />
          <Text
            label="Name"
            value={item.name}
            onChange={(v) => set({ name: v })}
            disabled={disabled}
          />
          <Text
            label="Role"
            value={item.role}
            onChange={(v) => set({ role: v })}
            placeholder="Founder & CEO"
            disabled={disabled}
          />
          <Area
            label="Bio"
            value={item.bio}
            onChange={(v) => set({ bio: v })}
            disabled={disabled}
          />
        </>
      )}
    />
  </>
);

/**
 * FULL_IMAGE: a heading + one full-width image (e.g. a group photo). Heading /
 * supporting line come from the section's title / subtitle fields; the image
 * comes from the media library.
 */
const FullImage = ({ data, onChange, disabled }: FormProps): React.JSX.Element => (
  <>
    <HeadingHint />
    <MediaDropField
      label="Image"
      kind="image"
      value={data.image_media_id as string}
      onChange={(id) => onChange({ ...data, image_media_id: id })}
      disabled={disabled}
    />
    <Choice
      label="Aspect ratio"
      value={
        data.aspect === 'ultrawide' || data.aspect === 'standard' ? (data.aspect as string) : 'wide'
      }
      options={[
        { value: 'ultrawide', label: 'Ultra-wide' },
        { value: 'wide', label: 'Wide' },
        { value: 'standard', label: 'Standard' },
      ]}
      onChange={(v) => onChange({ ...data, aspect: v })}
      disabled={disabled}
    />
    <Toggle
      label="Full-bleed"
      hint="Span the image edge-to-edge. Off = contained within the page gutters with rounded corners."
      checked={data.full_bleed !== false}
      onChange={(v) => onChange({ ...data, full_bleed: v })}
      disabled={disabled}
    />
  </>
);

/**
 * FEATURE_CARDS: a 3-column grid of identical cards. Heading + supporting line
 * come from the section's title / subtitle fields; each card is an uploaded
 * logo/icon + title + subtitle + a short italic description. Add cards in
 * multiples of 3 (3/6/9) for full rows.
 */
const FeatureCards = ({ data, onChange, disabled }: FormProps): React.JSX.Element => (
  <>
    <HeadingHint />
    <Text
      label="Eyebrow (guide pages only)"
      value={data.eyebrow}
      onChange={(v) => onChange({ ...data, eyebrow: v })}
      placeholder="Keep exploring"
      disabled={disabled}
    />
    <Choice
      label="Style"
      hint="Tile = soft cyan cards · Plain = centred, no card · Timeline = snaking year path · Steps = numbered circles (Application Process) · Trust row = small icon + title + one line, under a search hero"
      value={
        data.variant === 'plain' ||
        data.variant === 'timeline' ||
        data.variant === 'steps' ||
        data.variant === 'trust'
          ? (data.variant as 'plain' | 'timeline' | 'steps' | 'trust')
          : 'tile'
      }
      options={[
        { value: 'tile', label: 'Tile' },
        { value: 'plain', label: 'Plain' },
        { value: 'timeline', label: 'Timeline' },
        { value: 'steps', label: 'Steps' },
        { value: 'trust', label: 'Trust row' },
      ]}
      onChange={(v) => onChange({ ...data, variant: v })}
      disabled={disabled}
    />
    <Num
      label="Cards per row (desktop, 2-4)"
      value={typeof data.columns === 'number' ? (data.columns as number) : 3}
      min={2}
      max={4}
      onChange={(v) => onChange({ ...data, columns: v })}
      disabled={disabled}
    />
    <Repeater
      items={arr(data.items)}
      onItems={(items) => onChange({ ...data, items })}
      blank={() => ({
        icon_media_id: '',
        icon: '',
        image_media_id: '',
        title: '',
        subtitle: '',
        description: '',
        href: '',
      })}
      disabled={disabled}
      render={(item, set) => (
        <>
          <MediaDropField
            label="Logo / icon"
            kind="image"
            value={item.icon_media_id as string}
            onChange={(id) => set({ icon_media_id: id })}
            disabled={disabled}
          />
          {/* The trust row draws a built-in icon when no image is uploaded. */}
          {data.variant === 'trust' && (
            <IconPicker
              label="Icon (used when no image is uploaded)"
              value={item.icon}
              onChange={(v) => set({ icon: v })}
              disabled={disabled}
            />
          )}
          {/* Photo + link drive the guides' "Explore more" cards; the icon-tile
              looks above ignore both. */}
          <MediaDropField
            label="Card photo (guide pages only)"
            kind="image"
            value={item.image_media_id as string}
            onChange={(id) => set({ image_media_id: id })}
            disabled={disabled}
          />
          <Text
            label="Title"
            value={item.title}
            onChange={(v) => set({ title: v })}
            disabled={disabled}
          />
          <Text
            label="Link (guide pages only)"
            value={item.href}
            onChange={(v) => set({ href: v })}
            placeholder="/places-to-visit-in-bali"
            disabled={disabled}
          />
          <Text
            label="Subtitle"
            value={item.subtitle}
            onChange={(v) => set({ subtitle: v })}
            disabled={disabled}
          />
          <Area
            label="Description"
            value={item.description}
            onChange={(v) => set({ description: v })}
            disabled={disabled}
          />
        </>
      )}
    />
  </>
);

/**
 * PROCESS_STEPS: "Our Process" numbered step cards. Heading + supporting line
 * come from the section's title / subtitle fields; each step is a title + short
 * description (auto-numbered 01, 02, … by position). An optional CTA button
 * renders centred under the grid.
 */
const ProcessSteps = ({ data, onChange, disabled }: FormProps): React.JSX.Element => (
  <>
    <HeadingHint />
    <Num
      label="Steps per row (desktop, 2-4)"
      value={typeof data.columns === 'number' ? (data.columns as number) : 3}
      min={2}
      max={4}
      onChange={(v) => onChange({ ...data, columns: v })}
      disabled={disabled}
    />
    <Repeater
      items={arr(data.items)}
      onItems={(items) => onChange({ ...data, items })}
      blank={() => ({ title: '', description: '' })}
      disabled={disabled}
      render={(item, set) => (
        <>
          <Text
            label="Title"
            value={item.title}
            onChange={(v) => set({ title: v })}
            placeholder="Brief & Goals"
            disabled={disabled}
          />
          <Area
            label="Description"
            value={item.description}
            onChange={(v) => set({ description: v })}
            disabled={disabled}
          />
        </>
      )}
    />
    <div className="mt-1 text-xs font-semibold tracking-wide text-(--fg-muted) uppercase">
      Call to action (optional)
    </div>
    <Text
      label="Button label"
      value={data.cta_text}
      onChange={(v) => onChange({ ...data, cta_text: v })}
      placeholder="Get Proposal"
      disabled={disabled}
    />
    <Text
      label="Button link"
      value={data.cta_href}
      onChange={(v) => onChange({ ...data, cta_href: v })}
      placeholder="/contact"
      disabled={disabled}
    />
  </>
);

/**
 * VALUE_PROPS: "What's in it for you?" value-prop grid. Heading + supporting
 * line come from the section's title / subtitle fields; each item is a title +
 * short description, laid out in two columns with cycled accent bars.
 */
const ValueProps = ({ data, onChange, disabled }: FormProps): React.JSX.Element => (
  <>
    <HeadingHint />
    <Repeater
      items={arr(data.items)}
      onItems={(items) => onChange({ ...data, items })}
      blank={() => ({ title: '', description: '' })}
      disabled={disabled}
      render={(item, set) => (
        <>
          <Text
            label="Title"
            value={item.title}
            onChange={(v) => set({ title: v })}
            placeholder="Travel Perks"
            disabled={disabled}
          />
          <Area
            label="Description"
            value={item.description}
            onChange={(v) => set({ description: v })}
            disabled={disabled}
          />
        </>
      )}
    />
  </>
);

/**
 * STATS_STRIP: a bordered bar of figures ("What Makes Us Different?"). Heading +
 * supporting line come from the section's title / subtitle fields; each stat is
 * a headline value + a label beneath it.
 */
const StatsStrip = ({ data, onChange, disabled }: FormProps): React.JSX.Element => (
  <>
    <HeadingHint />
    <Repeater
      items={arr(data.items)}
      onItems={(items) => onChange({ ...data, items })}
      blank={() => ({ value: '', label: '', icon: '' })}
      disabled={disabled}
      render={(item, set) => (
        <>
          <Text
            label="Value"
            value={item.value}
            onChange={(v) => set({ value: v })}
            placeholder="100K+"
            disabled={disabled}
          />
          <Text
            label="Label"
            value={item.label}
            onChange={(v) => set({ label: v })}
            placeholder="Happy Travellers"
            disabled={disabled}
          />
          <IconPicker
            label="Icon (guide pages only)"
            value={item.icon}
            onChange={(v) => set({ icon: v })}
            disabled={disabled}
          />
        </>
      )}
    />
    <p className="text-[12px] text-(--fg-faint)">
      Icons are drawn only by the “Best time to visit” guides, where this section renders as the
      Weather Snapshot tiles. Every other page shows the plain figures bar.
    </p>
  </>
);

/**
 * IMAGE_MARQUEE: a row of uploaded images that slides as a continuous marquee
 * (e.g. a "Life at Utazzo" career-page strip). Heading + supporting line come
 * from the section's title / subtitle fields; upload 5-6 images and pick the
 * aspect + slider behaviour (shared with the logo strips).
 */
const ImageMarquee = ({ data, onChange, disabled }: FormProps): React.JSX.Element => {
  const ids = (data.photo_media_ids as string[] | undefined) ?? [];
  return (
    <>
      <HeadingHint />
      <MediaGalleryField
        label="Images"
        value={ids}
        onChange={(next) => onChange({ ...data, photo_media_ids: next })}
        disabled={disabled}
      />
      <p className="text-[12px] text-(--fg-faint)">
        Renders as a single row that slides as a continuous marquee. Drag thumbnails to reorder.
      </p>
      <Choice
        label="Aspect ratio"
        value={
          data.aspect === 'square' || data.aspect === 'portrait' || data.aspect === 'wide'
            ? (data.aspect as string)
            : 'landscape'
        }
        options={[
          { value: 'landscape', label: 'Landscape (4:3)' },
          { value: 'square', label: 'Square (1:1)' },
          { value: 'portrait', label: 'Portrait (3:4)' },
          { value: 'wide', label: 'Wide (16:9)' },
        ]}
        onChange={(v) => onChange({ ...data, aspect: v })}
        disabled={disabled}
      />
      <LogoSliderControls data={data} onChange={onChange} disabled={disabled} />
    </>
  );
};

/** Join a stored string[] into one-per-line text for a textarea. */
/**
 * OPEN_POSITIONS: careers block. Heading + supporting line come from the
 * section's title / subtitle fields; each opening is a job card (role, meta,
 * overview + responsibilities / preferred-skills bullets). Responsibilities and
 * preferred skills are edited one bullet per line.
 */
/**
 * The per-position field set (role, department, hiring branch, work mode,
 * experience, overview, responsibilities, preferred skills). Shared by the
 * inline OPEN_POSITIONS section editor (Repeater) and the admin Position-tab
 * "Add/Edit position" modal so both edit the identical shape. `set` merges a
 * partial patch into the item. Location is picked from the Branch master (we
 * hire for specific offices) so the website can show the branch's city.
 */
export const PositionFields = ({
  item,
  set,
  disabled,
}: {
  item: Data;
  set: (patch: Data) => void;
  disabled?: boolean;
}): React.JSX.Element => {
  const branchesQuery = useBranchesList({
    page: 1,
    pageSize: 200,
    isActive: true,
    sortBy: 'sortOrder',
    sortDir: 'asc',
  });
  const branchOptions = (branchesQuery.data?.data ?? []).map((b) => ({
    value: b.id,
    label: b.name,
    description: b.city?.name ?? b.officeBadge ?? undefined,
  }));
  return (
    <>
      <Text
        label="Role title"
        value={item.title}
        onChange={(v) => set({ title: v })}
        placeholder="Performance Specialist"
        disabled={disabled}
      />
      <Text
        label="Department (tab filter)"
        value={item.category}
        onChange={(v) => set({ category: v })}
        placeholder="Marketing"
        disabled={disabled}
      />
      <div>
        <UzCombobox
          label="Hiring branch (location)"
          placeholder={branchesQuery.isLoading ? 'Loading branches…' : 'Select a branch'}
          searchPlaceholder="Search branches…"
          options={branchOptions}
          value={typeof item.branch_id === 'string' && item.branch_id ? item.branch_id : undefined}
          onValueChange={(id) => set({ branch_id: id ?? '' })}
          disabled={disabled || branchesQuery.isLoading}
          emptyText="No branches"
        />
        <p className="mt-1 text-[12px] text-(--fg-faint)">
          The card shows this branch&apos;s city. Manage offices in{' '}
          <strong>Masters → Branches</strong>.
        </p>
      </div>
      <Text
        label="Work mode"
        value={item.work_mode}
        onChange={(v) => set({ work_mode: v })}
        placeholder="On-site / Remote / Hybrid"
        disabled={disabled}
      />
      <Text
        label="Experience"
        value={item.experience}
        onChange={(v) => set({ experience: v })}
        placeholder="1-3 Years"
        disabled={disabled}
      />
      <Num
        label="Open roles (shown as a badge; 0 hides it)"
        value={typeof item.openings === 'number' ? item.openings : 1}
        onChange={(v) => set({ openings: Number.isFinite(v) ? v : 0 })}
        min={0}
        max={999}
        disabled={disabled}
      />
      <Area
        label="Job overview"
        value={item.overview}
        onChange={(v) => set({ overview: v })}
        disabled={disabled}
      />
      <Area
        label="Roles & responsibilities (one per line)"
        value={linesToText(item.responsibilities)}
        onChange={(v) => set({ responsibilities: textToLines(v) })}
        disabled={disabled}
      />
      <Area
        label="Preferred skills & qualifications (one per line)"
        value={linesToText(item.preferred_skills)}
        onChange={(v) => set({ preferred_skills: textToLines(v) })}
        disabled={disabled}
      />
    </>
  );
};

// The individual openings are edited in the dedicated "Position" tab (a table +
// modal over this same section's items). This section form only carries the
// heading/subheading; the inline item editor was removed to avoid two surfaces.
const OpenPositions = (): React.JSX.Element => (
  <>
    <HeadingHint />
    <p className="rounded-md border border-dashed border-(--border-strong) p-3 text-xs text-(--fg-faint)">
      Job openings are managed in the <strong>Position</strong> tab above.
    </p>
  </>
);

/** The autoplay/loop/per-view block shared by every carousel-style section. */
const SliderControls = ({ data, onChange, disabled }: FormProps): React.JSX.Element => {
  const bool = (k: string, dflt = true): boolean =>
    data[k] === undefined ? dflt : data[k] === true;
  const num = (k: string, dflt: number): number =>
    typeof data[k] === 'number' ? (data[k] as number) : dflt;
  return (
    <>
      <div className="mt-1 text-xs font-semibold tracking-wide text-(--fg-muted) uppercase">
        Slider
      </div>
      <Toggle
        label="Autoplay"
        checked={bool('autoplay', false)}
        onChange={(v) => onChange({ ...data, autoplay: v })}
        disabled={disabled}
      />
      {bool('autoplay', false) && (
        <Num
          label="Autoplay interval (ms)"
          value={num('autoplay_ms', 5000)}
          min={1000}
          max={20000}
          onChange={(v) => onChange({ ...data, autoplay_ms: v })}
          disabled={disabled}
        />
      )}
      <Toggle
        label="Pause on hover"
        checked={bool('pause_on_hover')}
        onChange={(v) => onChange({ ...data, pause_on_hover: v })}
        disabled={disabled}
      />
      <Toggle
        label="Loop"
        checked={bool('loop')}
        onChange={(v) => onChange({ ...data, loop: v })}
        disabled={disabled}
      />
      <Toggle
        label="Show arrows"
        checked={bool('show_arrows')}
        onChange={(v) => onChange({ ...data, show_arrows: v })}
        disabled={disabled}
      />
      <Toggle
        label="Show dots"
        checked={bool('show_dots', false)}
        onChange={(v) => onChange({ ...data, show_dots: v })}
        disabled={disabled}
      />
      <div className="grid grid-cols-3 gap-2">
        <Num
          label="Per view · desktop"
          value={num('per_view_desktop', 4)}
          min={1}
          max={6}
          onChange={(v) => onChange({ ...data, per_view_desktop: v })}
          disabled={disabled}
        />
        <Num
          label="Tablet"
          value={num('per_view_tablet', 2)}
          min={1}
          max={4}
          onChange={(v) => onChange({ ...data, per_view_tablet: v })}
          disabled={disabled}
        />
        <Num
          label="Mobile"
          value={num('per_view_mobile', 1)}
          min={1}
          max={2}
          onChange={(v) => onChange({ ...data, per_view_mobile: v })}
          disabled={disabled}
        />
      </div>
    </>
  );
};

const HandpickedStays = ({ data, onChange, disabled }: FormProps): React.JSX.Element => (
  <>
    <HeadingHint />
    <Repeater
      items={arr(data.items)}
      onItems={(items) => onChange({ ...data, items })}
      blank={() => ({
        image_media_id: '',
        hotel_name: '',
        occupancy_text: '1 Night, 2 Adults',
        price_text: '',
        href: '/',
      })}
      disabled={disabled}
      render={(item, set) => (
        <>
          <MediaDropField
            label="Photo"
            kind="image"
            value={item.image_media_id as string}
            onChange={(id) => set({ image_media_id: id })}
            disabled={disabled}
          />
          <Text
            label="Hotel name"
            value={item.hotel_name}
            onChange={(v) => set({ hotel_name: v })}
            disabled={disabled}
          />
          <Text
            label="Occupancy (chip line 1)"
            value={item.occupancy_text}
            onChange={(v) => set({ occupancy_text: v })}
            placeholder="1 Night, 2 Adults"
            disabled={disabled}
          />
          <Text
            label="Price (chip line 2)"
            value={item.price_text}
            onChange={(v) => set({ price_text: v })}
            placeholder="₹9,217"
            disabled={disabled}
          />
          <Text
            label="Link"
            value={item.href}
            onChange={(v) => set({ href: v })}
            placeholder="/stays/itc-hotels"
            disabled={disabled}
          />
        </>
      )}
    />
    <SliderControls data={data} onChange={onChange} disabled={disabled} />
  </>
);

const ExploreThemes = ({ data, onChange, disabled }: FormProps): React.JSX.Element => (
  <>
    <HeadingHint />
    <Repeater
      items={arr(data.items)}
      onItems={(items) => onChange({ ...data, items })}
      blank={() => ({ image_media_id: '', label: '', href: '/' })}
      disabled={disabled}
      render={(item, set) => (
        <>
          <MediaDropField
            label="Tile image"
            kind="image"
            value={item.image_media_id as string}
            onChange={(id) => set({ image_media_id: id })}
            disabled={disabled}
          />
          <Text
            label="Theme label"
            value={item.label}
            onChange={(v) => set({ label: v })}
            placeholder="Adventure"
            disabled={disabled}
          />
          <Text
            label="Link"
            value={item.href}
            onChange={(v) => set({ href: v })}
            placeholder="/themes/adventure"
            disabled={disabled}
          />
        </>
      )}
    />
    <SliderControls data={data} onChange={onChange} disabled={disabled} />
  </>
);

/**
 * REELS_STRIP: each card is either an uploaded video (lightbox) or a YouTube
 * Shorts / Instagram Reel link (plays inline, cover auto-fetched). Fill one -
 * the link wins if both are set. The CTA pill across the card's bottom needs
 * both a label and a route.
 */
const ReelsStripForm = ({ data, onChange, disabled }: FormProps): React.JSX.Element => (
  <>
    <HeadingHint />
    <Repeater
      items={arr(data.items)}
      onItems={(items) => onChange({ ...data, items })}
      blank={() => ({
        reel_url: '',
        video_media_id: '',
        poster_media_id: '',
        caption: '',
        tag: '',
        cta_label: 'View Package',
        cta_href: '',
      })}
      disabled={disabled}
      render={(item, set) => <ReelsStripRow item={item} set={set} disabled={disabled} />}
    />
  </>
);

/** One REELS_STRIP card. Its own component so the reel-check hook can live here. */
const ReelsStripRow = ({
  item,
  set,
  disabled,
}: {
  item: Data;
  set: (patch: Data) => void;
  disabled?: boolean;
}): React.JSX.Element => {
  const reelUrl = typeof item.reel_url === 'string' ? item.reel_url : '';
  const { state, check } = useReelCheck(reelUrl);
  const liveError = reelCheckError(state);
  const isInstagram = reelCheckProvider(state) === 'instagram';

  return (
    <>
      <Text
        label="Reel link (YouTube Shorts / Instagram Reel)"
        value={item.reel_url}
        onChange={(v) => set({ reel_url: v })}
        placeholder="https://www.youtube.com/shorts/…"
        disabled={disabled}
        error={liveError}
        inputClassName={liveError ? undefined : reelCheckBorderClass(state)}
      />
      <ReelCheckBar state={state} url={reelUrl} onFetch={check} disabled={disabled} />
      <p className="text-[12px] text-(--fg-faint)">
        {isInstagram
          ? 'Instagram gives no reliable auto thumbnail - upload a vertical cover below.'
          : 'Cover image is fetched automatically. Leave the upload below empty unless you want a custom cover.'}
      </p>
      {!reelUrl.trim() && (
        <MediaDropField
          label="Video (only if you are not using a reel link)"
          kind="video"
          value={item.video_media_id as string}
          onChange={(id) => set({ video_media_id: id })}
          disabled={disabled}
        />
      )}
      <MediaDropField
        label="Cover image (optional)"
        kind="image"
        value={item.poster_media_id as string}
        onChange={(id) => set({ poster_media_id: id })}
        disabled={disabled}
      />
      <Text
        label="Tag chip (optional)"
        value={item.tag}
        onChange={(v) => set({ tag: v })}
        placeholder="Kashmir"
        disabled={disabled}
      />
      <Text
        label="CTA label (optional)"
        value={item.cta_label}
        onChange={(v) => set({ cta_label: v })}
        placeholder="View Package"
        disabled={disabled}
      />
      <Text
        label="CTA link"
        value={item.cta_href}
        onChange={(v) => set({ cta_href: v })}
        placeholder="/kashmir-tour-packages/srinagar-6-days"
        disabled={disabled}
      />
      <Text
        label="Caption (optional)"
        value={item.caption}
        onChange={(v) => set({ caption: v })}
        disabled={disabled}
      />
    </>
  );
};

/**
 * RICH_TEXT: a free-form WYSIWYG prose block for long-form legal / policy copy
 * (Terms & Conditions, Privacy Policy, "About" pages). The heading comes from
 * the section's title column; the body is edited with the shared RichTextEditor
 * (same one the blog uses) and stored as HTML. `read_more` collapses long
 * content behind a "Read more" expander on the website.
 */
const RichText = ({ data, onChange, disabled }: FormProps): React.JSX.Element => (
  <>
    <HeadingHint />
    <div>
      <label className="mb-1 block text-xs font-medium text-(--fg-muted)">Content</label>
      <RichTextEditor
        value={typeof data.html === 'string' ? data.html : ''}
        onChange={(html) => onChange({ ...data, html })}
        placeholder="Write your terms, privacy policy, or long-form content…"
        folder="custom-pages"
        disabled={disabled}
        stickyTop="flush"
      />
    </div>
    <Toggle
      label="Collapse behind “Read more”"
      hint="Show a few lines on the website with a Read more expander."
      checked={data.read_more === true}
      onChange={(v) => onChange({ ...data, read_more: v })}
      disabled={disabled}
    />
    <Toggle
      label="Show in a card"
      hint="Heading + text inside a white rounded card with a soft shadow (like the FAQ block). Off = plain document."
      checked={data.card === true}
      onChange={(v) => onChange({ ...data, card: v })}
      disabled={disabled}
    />
  </>
);

/**
 * QUICK_INFO: "Quick Information" key/value panel. Heading comes from the
 * section's title field; each row is a label + value (e.g. "Countries Covered"
 * → "40+").
 */
const QuickInfo = ({ data, onChange, disabled }: FormProps): React.JSX.Element => (
  <>
    <HeadingHint />
    <Repeater
      items={arr(data.items)}
      onItems={(items) => onChange({ ...data, items })}
      blank={() => ({ label: '', value: '' })}
      disabled={disabled}
      render={(item, set) => (
        <>
          <Text
            label="Label"
            value={item.label}
            onChange={(v) => set({ label: v })}
            placeholder="Countries Covered"
            disabled={disabled}
          />
          <Text
            label="Value"
            value={item.value}
            onChange={(v) => set({ value: v })}
            placeholder="40+"
            disabled={disabled}
          />
        </>
      )}
    />
  </>
);

/**
 * BEST_TIME: "Best time to visit" - a best-window callout, a JAN-DEC month
 * strip tinted by season, a supporting paragraph and optional trust stats.
 * Heading + supporting line come from the section's title / subtitle fields.
 */
const BestTime = ({ data, onChange, disabled }: FormProps): React.JSX.Element => (
  <>
    <HeadingHint />
    <Text
      label="Eyebrow (guide pages only)"
      value={data.eyebrow}
      onChange={(v) => onChange({ ...data, eyebrow: v })}
      placeholder="12-month overview"
      disabled={disabled}
    />
    <Text
      label="Callout label"
      value={data.highlight_label}
      onChange={(v) => onChange({ ...data, highlight_label: v })}
      placeholder="Best time to visit"
      disabled={disabled}
    />
    <Text
      label="Best window"
      value={data.highlight_value}
      onChange={(v) => onChange({ ...data, highlight_value: v })}
      placeholder="April - November"
      disabled={disabled}
    />
    <Area
      label="Supporting paragraph"
      value={data.body}
      onChange={(v) => onChange({ ...data, body: v })}
      disabled={disabled}
    />
    <div className="mt-1 text-xs font-semibold tracking-wide text-(--fg-muted) uppercase">
      Months (JAN-DEC)
    </div>
    <Repeater
      items={arr(data.months)}
      onItems={(months) => onChange({ ...data, months })}
      blank={() => ({ label: '', active: false, icon: '', temp: '', sunshine: '', rainfall: '' })}
      disabled={disabled}
      render={(item, set) => (
        <>
          <Text
            label="Month label"
            value={item.label}
            onChange={(v) => set({ label: v })}
            placeholder="APR"
            disabled={disabled}
          />
          <Toggle
            label="Highlighted"
            hint="Filled dark when the month is part of the best window"
            checked={item.active === true}
            onChange={(v) => set({ active: v })}
            disabled={disabled}
          />
          {/* Card fields - drawn only by the "Best time to visit" guides, where
              this section renders as the month calendar. */}
          <IconPicker
            label="Card icon (guide pages only)"
            value={item.icon}
            onChange={(v) => set({ icon: v })}
            disabled={disabled}
          />
          <Text
            label="Temperature"
            value={item.temp}
            onChange={(v) => set({ temp: v })}
            placeholder="27°C"
            disabled={disabled}
          />
          <Text
            label="Sunshine"
            value={item.sunshine}
            onChange={(v) => set({ sunshine: v })}
            placeholder="High"
            disabled={disabled}
          />
          <Text
            label="Rainfall"
            value={item.rainfall}
            onChange={(v) => set({ rainfall: v })}
            placeholder="Mid"
            disabled={disabled}
          />
        </>
      )}
    />
    <div className="mt-1 text-xs font-semibold tracking-wide text-(--fg-muted) uppercase">
      Trust stats (optional)
    </div>
    <Repeater
      items={arr(data.stats)}
      onItems={(stats) => onChange({ ...data, stats })}
      blank={() => ({ value: '', label: '' })}
      disabled={disabled}
      render={(item, set) => (
        <>
          <Text
            label="Value"
            value={item.value}
            onChange={(v) => set({ value: v })}
            placeholder="50k+"
            disabled={disabled}
          />
          <Text
            label="Label"
            value={item.label}
            onChange={(v) => set({ label: v })}
            placeholder="Happy Travellers"
            disabled={disabled}
          />
        </>
      )}
    />
  </>
);

/**
 * MONTH_BY_MONTH: the guides' long-form month cards - a photo beside the
 * month's temperature, summary, pros / cons, activities, festivals and travel
 * tip. Pros / cons / activities are one entry per line. "Read more" text is
 * optional; leaving it empty hides the toggle on the website.
 */
const MonthByMonth = ({ data, onChange, disabled }: FormProps): React.JSX.Element => (
  <>
    <HeadingHint />
    <Text
      label="Eyebrow"
      value={data.eyebrow}
      onChange={(v) => onChange({ ...data, eyebrow: v })}
      placeholder="Month by month"
      disabled={disabled}
    />
    <Repeater
      items={arr(data.items)}
      onItems={(items) => onChange({ ...data, items })}
      blank={() => ({
        image_media_id: '',
        name: '',
        temp: '',
        description: '',
        pros: [],
        cons: [],
        activities: [],
        festivals: '',
        travel_tip: '',
        details: '',
      })}
      disabled={disabled}
      render={(item, set) => (
        <>
          <MediaDropField
            label="Photo"
            kind="image"
            value={item.image_media_id as string}
            onChange={(id) => set({ image_media_id: id })}
            disabled={disabled}
          />
          <div className="grid grid-cols-2 gap-2">
            <Text
              label="Month"
              value={item.name}
              onChange={(v) => set({ name: v })}
              placeholder="January"
              disabled={disabled}
            />
            <Text
              label="Temperature"
              value={item.temp}
              onChange={(v) => set({ temp: v })}
              placeholder="24 - 31°C"
              disabled={disabled}
            />
          </div>
          <Area
            label="Summary"
            value={item.description}
            onChange={(v) => set({ description: v })}
            disabled={disabled}
          />
          <Area
            label="Pros (one per line)"
            value={linesToText(item.pros)}
            onChange={(v) => set({ pros: textToLines(v) })}
            disabled={disabled}
          />
          <Area
            label="Cons (one per line)"
            value={linesToText(item.cons)}
            onChange={(v) => set({ cons: textToLines(v) })}
            disabled={disabled}
          />
          <Area
            label="Activities (one per line)"
            value={linesToText(item.activities)}
            onChange={(v) => set({ activities: textToLines(v) })}
            disabled={disabled}
          />
          <Text
            label="Festivals"
            value={item.festivals}
            onChange={(v) => set({ festivals: v })}
            placeholder="Galungan (varies)"
            disabled={disabled}
          />
          <Area
            label="Travel tip"
            value={item.travel_tip}
            onChange={(v) => set({ travel_tip: v })}
            disabled={disabled}
          />
          <Area
            label="Read more text (optional)"
            value={item.details}
            onChange={(v) => set({ details: v })}
            disabled={disabled}
          />
        </>
      )}
    />
  </>
);

/**
 * RECOMMENDED_PACKAGES: "Recommended <Destination> Tour Packages". Pick the
 * destination, then the packages to feature; every card field (photo, name,
 * price, inclusion lines) is read live from the package master at render, so
 * nothing here duplicates package content. Leaving the pick-list empty falls
 * back to the newest packages for the destination, and "View all" points at the
 * destination's own page unless an href is given.
 */
const RecommendedPackages = ({ data, onChange, disabled }: FormProps): React.JSX.Element => {
  const destQuery = useDestinationsList({
    page: 1,
    pageSize: 200,
    sortBy: 'name',
    sortDir: 'asc',
  });
  const destOptions = (destQuery.data?.data ?? []).map((d) => ({ value: d.id, label: d.name }));
  const destId = typeof data.destination_id === 'string' ? data.destination_id : '';

  // The pick-list narrows to the chosen destination so an editor isn't scrolling
  // the whole catalogue to find the Bali packages.
  const packagesQuery = usePackagesList({
    page: 1,
    pageSize: 200,
    sortBy: 'updatedAt',
    sortDir: 'desc',
    status: 'ACTIVE',
    ...(destId ? { destinationId: destId } : {}),
  });
  const packageOptions = (packagesQuery.data?.data ?? []).map((p) => ({
    id: p.id,
    label: p.name,
    description: p.slug,
  }));
  const picked = Array.isArray(data.package_ids)
    ? (data.package_ids as unknown[]).filter((v): v is string => typeof v === 'string')
    : [];

  return (
    <>
      <HeadingHint />
      <Text
        label="Eyebrow"
        value={data.eyebrow}
        onChange={(v) => onChange({ ...data, eyebrow: v })}
        placeholder="Hand-picked"
        disabled={disabled}
      />
      <UzCombobox
        name="recpkg-destination"
        label="Destination"
        placeholder="Select a destination…"
        searchPlaceholder="Search destinations…"
        value={destId}
        onValueChange={(v) => onChange({ ...data, destination_id: v })}
        options={destOptions}
        loading={destQuery.isLoading}
        disabled={disabled}
      />
      <OrderedPickerList
        options={packageOptions}
        value={picked}
        onChange={(ids) => onChange({ ...data, package_ids: ids })}
        loading={packagesQuery.isLoading}
        disabled={disabled}
        maxSelected={12}
        pickerLabel="Packages"
        pickerHint={
          destId
            ? 'Active packages for this destination. Pick the ones to feature, then drag to set card order.'
            : 'Pick a destination above to narrow this list. Leave empty to auto-fill with the newest packages.'
        }
        searchPlaceholder="Search packages…"
        sortLabel="Card order"
        sortEmptyMessage="Nothing picked - the row auto-fills with the newest packages for the destination."
      />
      <Num
        label="Auto-fill count (used only when nothing is picked)"
        value={data.limit}
        onChange={(v) => onChange({ ...data, limit: v })}
        min={1}
        max={12}
        disabled={disabled}
      />
      <div className="grid grid-cols-2 gap-2">
        <Text
          label="Card CTA label"
          value={data.cta_label}
          onChange={(v) => onChange({ ...data, cta_label: v })}
          placeholder="View Package"
          disabled={disabled}
        />
        <Text
          label="View-all label (empty hides it)"
          value={data.view_all_label}
          onChange={(v) => onChange({ ...data, view_all_label: v })}
          placeholder="View all"
          disabled={disabled}
        />
      </div>
      <Text
        label="View-all link (optional - defaults to the destination page)"
        value={data.view_all_href}
        onChange={(v) => onChange({ ...data, view_all_href: v })}
        placeholder="/international/bali"
        disabled={disabled}
      />
      <div className="grid grid-cols-2 gap-2">
        <Text
          label="Price prefix"
          value={data.price_prefix}
          onChange={(v) => onChange({ ...data, price_prefix: v })}
          placeholder="Starting from"
          disabled={disabled}
        />
        <Text
          label="Price suffix"
          value={data.price_suffix}
          onChange={(v) => onChange({ ...data, price_suffix: v })}
          placeholder="/ person"
          disabled={disabled}
        />
      </div>
      <Num
        label="Inclusion lines per card (1-8)"
        value={data.bullets_per_card}
        onChange={(v) => onChange({ ...data, bullets_per_card: v })}
        min={1}
        max={8}
        disabled={disabled}
      />
    </>
  );
};

/**
 * TRAVEL_STYLES: "Best time based on travel style" tiles - an icon, the style's
 * name, a line of copy and the months that suit it. Heading + supporting line
 * come from the section's title / subtitle fields.
 */
const TravelStyles = ({ data, onChange, disabled }: FormProps): React.JSX.Element => (
  <>
    <HeadingHint />
    <Text
      label="Eyebrow"
      value={data.eyebrow}
      onChange={(v) => onChange({ ...data, eyebrow: v })}
      placeholder="Choose your trip"
      disabled={disabled}
    />
    <Repeater
      items={arr(data.items)}
      onItems={(items) => onChange({ ...data, items })}
      blank={() => ({ icon: '', title: '', description: '', when: '' })}
      disabled={disabled}
      render={(item, set) => (
        <>
          <IconPicker
            label="Icon"
            value={item.icon}
            onChange={(v) => set({ icon: v })}
            disabled={disabled}
          />
          <Text
            label="Travel style"
            value={item.title}
            onChange={(v) => set({ title: v })}
            placeholder="Honeymoon"
            disabled={disabled}
          />
          <Area
            label="Description"
            value={item.description}
            onChange={(v) => set({ description: v })}
            disabled={disabled}
          />
          <Text
            label="Best months"
            value={item.when}
            onChange={(v) => set({ when: v })}
            placeholder="Apr - Jun"
            disabled={disabled}
          />
        </>
      )}
    />
  </>
);

/**
 * The description an editor should open on: the authored HTML, or - for a card
 * written before this field became rich text - the legacy plain text wrapped in
 * paragraphs, so the editor starts on the copy that's already live. Nothing is
 * written back until the author edits, so an untouched card keeps rendering
 * from its plain text on the site.
 */
const placeBodyHtml = (item: Data): string => {
  const html = typeof item.description_html === 'string' ? item.description_html : '';
  if (html) return html;
  const plain = typeof item.description === 'string' ? item.description : '';
  return plain ? plainTextToHtml(plain) : '';
};

/** Preview styling for a closed body - the tags the editor can produce, small. */
const BODY_PREVIEW_PROSE = [
  'text-[13px] leading-relaxed text-(--fg-muted)',
  '[&_p]:mb-1.5 [&>:first-child]:mt-0 [&>:last-child]:mb-0',
  '[&_strong]:font-semibold [&_em]:italic',
  '[&_h1]:text-[15px] [&_h2]:text-[14px] [&_h3]:text-[14px] [&_h4]:text-[13px]',
  '[&_h1]:mt-2 [&_h2]:mt-2 [&_h3]:mt-2 [&_h4]:mt-2 [&_h5]:mt-2 [&_h6]:mt-2',
  '[&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-semibold [&_h4]:font-semibold',
  '[&_h1]:text-(--fg) [&_h2]:text-(--fg) [&_h3]:text-(--fg) [&_h4]:text-(--fg)',
  '[&_ul]:my-1.5 [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:my-1.5 [&_ol]:list-decimal [&_ol]:pl-4',
  '[&_a]:text-(--primary) [&_a]:underline [&_a]:pointer-events-none',
  '[&_blockquote]:my-1.5 [&_blockquote]:border-l-2 [&_blockquote]:border-(--border) [&_blockquote]:pl-2 [&_blockquote]:italic',
  '[&_table]:my-1.5 [&_table]:block [&_table]:overflow-x-auto [&_td]:border [&_td]:border-(--border) [&_td]:p-1',
  '[&_th]:border [&_th]:border-(--border) [&_th]:p-1 [&_th]:text-left [&_th]:font-semibold',
  '[&_img]:my-1.5 [&_img]:max-h-24 [&_img]:w-auto [&_img]:rounded',
].join(' ');

/**
 * A rich-text field inside a repeater, where the editor is built only once the
 * author opens that item.
 *
 * Quill is expensive to construct - a toolbar, a contenteditable root and a
 * mutation observer each - and a guide legitimately runs to 50+ places. One
 * editor per item meant every place on the page built one before the tab could
 * paint, which is a multi-second render for a list this long (and, under
 * StrictMode, twice that). A closed item shows its formatted body as plain
 * markup instead, which costs nothing, and only the place actually being
 * written carries an editor.
 *
 * The preview is not a substitute for the editor: it is the same HTML, so what
 * an author sees closed is what they get open.
 */
const DeferredRichText = ({
  label,
  value,
  onChange,
  placeholder,
  disabled,
  folder,
}: {
  label: string;
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
  folder?: string;
}): React.JSX.Element => {
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <div className="mb-1 flex items-center justify-between gap-2">
        <label className="block text-xs font-medium text-(--fg-muted)">{label}</label>
        {!disabled && (
          <button
            type="button"
            className="text-xs font-medium text-(--primary) hover:underline"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? 'Done' : value ? 'Edit' : 'Write'}
          </button>
        )}
      </div>
      {open ? (
        <RichTextEditor
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          folder={folder}
          disabled={disabled}
          stickyTop="flush"
          size="compact"
        />
      ) : value ? (
        // Author-written markup from this same form - not third-party input.
        // Links are click-through-disabled so the preview stays a preview.
        <div
          className={`max-h-56 overflow-y-auto rounded-lg border border-(--border) bg-(--z-50) p-3 ${BODY_PREVIEW_PROSE}`}
          dangerouslySetInnerHTML={{ __html: value }}
        />
      ) : (
        <div className="rounded-lg border border-dashed border-(--border) p-3 text-[13px] text-(--fg-faint)">
          {placeholder ?? 'Nothing written yet.'}
        </div>
      )}
    </>
  );
};

/** The three fixed meta fields a card carried before the row became free-form. */
const LEGACY_PLACE_META: { key: string; label: string }[] = [
  { key: 'best_time', label: 'Best time to visit' },
  { key: 'timings', label: 'Timings' },
  { key: 'entry_fee', label: 'Entry Fee' },
];

/**
 * The meta row an editor should open on: the place's own label/value pairs, or
 * - for a card written against the three fixed fields - those fields under the
 * labels the site already renders them with, so opening an old card shows the
 * row exactly as it appears live. Nothing is written back until the author
 * edits, so an untouched card keeps rendering from the legacy fields.
 */
const placeMetaRows = (item: Data): Data[] => {
  const rows = Array.isArray(item.meta) ? (item.meta as Data[]) : [];
  if (rows.length > 0) return rows;
  return LEGACY_PLACE_META.map(({ key, label }) => ({
    label,
    value: typeof item[key] === 'string' ? item[key] : '',
  })).filter((row) => row.value);
};

/**
 * The card's meta row, as label/value pairs the author names themselves.
 *
 * Fixed fields can't cover it: a city wants "Best time to visit" and "Days
 * needed", a temple wants "Timings" and "Entry fee", a night market wants
 * neither. Writing any row clears the three legacy fields, so the two can never
 * disagree about what the card shows.
 */
const PlaceMetaEditor = ({
  rows,
  onRows,
  disabled,
}: {
  rows: Data[];
  onRows: (next: Data[]) => void;
  disabled?: boolean;
}): React.JSX.Element => (
  <div>
    <label className="mb-1 block text-xs font-medium text-(--fg-muted)">
      Card details (optional)
    </label>
    <div className="flex flex-col gap-2">
      {rows.map((row, i) => (
        <div key={i} className="flex items-center gap-2">
          <UzInput
            value={typeof row.label === 'string' ? row.label : ''}
            placeholder="Best time to visit"
            disabled={disabled}
            className="flex-1"
            onChange={(e) =>
              onRows(rows.map((r, idx) => (idx === i ? { ...r, label: e.target.value } : r)))
            }
          />
          <UzInput
            value={typeof row.value === 'string' ? row.value : ''}
            placeholder="Apr–Oct"
            disabled={disabled}
            className="flex-1"
            onChange={(e) =>
              onRows(rows.map((r, idx) => (idx === i ? { ...r, value: e.target.value } : r)))
            }
          />
          {!disabled && (
            <button
              type="button"
              className="text-(--fg-faint) hover:text-(--ds-error)"
              onClick={() => onRows(rows.filter((_, idx) => idx !== i))}
              aria-label="Remove detail"
              title="Remove"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ))}
      {/* Six is two rows of three on the card - past that it stops being a
          glanceable meta strip and belongs in the description. */}
      {!disabled && rows.length < 6 && (
        <button
          type="button"
          onClick={() => onRows([...rows, { label: '', value: '' }])}
          className="flex items-center justify-center gap-1.5 rounded-md border border-dashed border-(--border-strong) py-2 text-xs font-medium text-(--fg-muted) hover:border-(--primary) hover:text-(--primary)"
        >
          <Plus className="h-3.5 w-3.5" /> Add detail
        </button>
      )}
    </div>
    <p className="mt-1 text-[12px] text-(--fg-faint)">
      The small strip at the bottom of the card. Name each detail yourself - “Days needed”,
      “Location”, “Entry fee” - so every place carries only what suits it. Leave it empty and the
      strip doesn’t show at all.
    </p>
  </div>
);

const isHeadingRow = (item: Data): boolean => item.is_heading === true;
const isPackagesRow = (item: Data): boolean => item.is_packages === true;
const isPlaceRow = (item: Data): boolean => !isHeadingRow(item) && !isPackagesRow(item);

/**
 * The places list as the editor shows it: one node per top-level block.
 *
 * `loose` is a single place with no heading above it - every place on a guide
 * nobody has divided yet, or a straggler before the first heading. One place
 * per node rather than a run, so the top level is a single drag list whatever
 * the list holds: sections drag as a whole, and an undivided list is just its
 * places, dragging directly.
 */
type PlaceNode =
  | { kind: 'group'; headingIndex: number; placeIndexes: number[] }
  | { kind: 'packages'; index: number }
  | { kind: 'loose'; index: number };

/**
 * Slice the flat array into blocks for display. The array stays the source of
 * truth - every node just holds indexes into it - so ordering, moving and
 * deleting all still operate on the one list the schema stores.
 *
 * Unlike the website's grouping, this keeps empty groups and blank headings:
 * you have to be able to see a half-finished section in order to finish it.
 */
const buildPlaceNodes = (items: Data[]): PlaceNode[] => {
  const nodes: PlaceNode[] = [];
  let group: { headingIndex: number; placeIndexes: number[] } | null = null;

  const closeGroup = (): void => {
    if (group) {
      nodes.push({ kind: 'group', ...group });
      group = null;
    }
  };

  items.forEach((item, i) => {
    if (isPackagesRow(item)) {
      closeGroup();
      nodes.push({ kind: 'packages', index: i });
      return;
    }
    if (isHeadingRow(item)) {
      closeGroup();
      group = { headingIndex: i, placeIndexes: [] };
      return;
    }
    if (group) group.placeIndexes.push(i);
    else nodes.push({ kind: 'loose', index: i });
  });
  closeGroup();

  return nodes;
};

/** The entries one node covers, in order - used to move a whole block at once. */
const nodeItems = (node: PlaceNode, items: Data[]): Data[] => {
  if (node.kind === 'group') {
    return [items[node.headingIndex]!, ...node.placeIndexes.map((i) => items[i]!)];
  }
  return [items[node.index]!];
};

/** Last flat index a node covers, so "add to this section" lands inside it. */
const nodeEndIndex = (node: PlaceNode): number => {
  if (node.kind === 'group') return node.placeIndexes.at(-1) ?? node.headingIndex;
  return node.index;
};

/** dnd-kit ids. Prefixed so one drag context can hold both levels at once. */
const nodeDragId = (nodeIndex: number): string => `node:${nodeIndex}`;
const placeDragId = (flatIndex: number): string => `place:${flatIndex}`;

/**
 * The stored list after a drop, or null when the drop changes nothing.
 *
 * Both levels of the editor share one drag context, so the id prefix decides
 * which list the drop belongs to: `node:` reorders the top level, where a
 * section moves with every place it owns; `place:` reorders within one open
 * section. A drag that somehow crosses the two is ignored rather than guessed
 * at - the index spaces are different and a guess would scramble the list.
 *
 * Pure, and separate from the component, because this is the part of drag and
 * drop worth testing: the pointer maths belongs to dnd-kit, but which entries
 * end up where is ours.
 */
export const reorderPlaceEntries = (
  items: Data[],
  activeId: string,
  overId: string | null,
): Data[] | null => {
  if (!overId || activeId === overId) return null;

  const parse = (id: string, prefix: string): number | null => {
    if (!id.startsWith(prefix)) return null;
    const n = Number(id.slice(prefix.length));
    return Number.isInteger(n) ? n : null;
  };

  const fromNode = parse(activeId, 'node:');
  const toNode = parse(overId, 'node:');
  if (fromNode !== null && toNode !== null) {
    const nodes = buildPlaceNodes(items);
    if (fromNode >= nodes.length || toNode >= nodes.length) return null;
    return arrayMove(nodes, fromNode, toNode).flatMap((n) => nodeItems(n, items));
  }

  const fromPlace = parse(activeId, 'place:');
  const toPlace = parse(overId, 'place:');
  if (fromPlace !== null && toPlace !== null) {
    if (fromPlace >= items.length || toPlace >= items.length) return null;
    return arrayMove(items, fromPlace, toPlace);
  }

  return null;
};

/** The grab handle on a draggable row. */
const DragHandle = ({
  label,
  ...rest
}: { label: string } & React.HTMLAttributes<HTMLButtonElement>): React.JSX.Element => (
  <button
    type="button"
    aria-label={label}
    title="Drag to reorder"
    className="shrink-0 cursor-grab text-(--fg-faint) hover:text-(--fg) active:cursor-grabbing"
    {...rest}
  >
    <GripVertical className="h-3.5 w-3.5" />
  </button>
);

/**
 * A place card, draggable by its handle.
 *
 * `dragId` is what decides which list it belongs to: a place inside a section
 * sorts among that section's places, while an ungrouped place sorts at the top
 * level alongside the sections themselves.
 */
const SortablePlaceCard = ({
  dragId,
  flatIndex,
  label,
  items,
  onItems,
  disabled,
  children,
}: {
  dragId: string;
  flatIndex: number;
  label: string;
  items: Data[];
  onItems: (next: Data[]) => void;
  disabled?: boolean;
  children: React.ReactNode;
}): React.JSX.Element => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: dragId,
    disabled,
  });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`rounded-md border border-(--border) bg-(--z-50) p-3 ${
        isDragging ? 'z-10 opacity-60 shadow-md' : ''
      }`}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          {!disabled && <DragHandle label={`Reorder ${label}`} {...attributes} {...listeners} />}
          <span className="truncate text-xs font-medium text-(--fg-faint)">{label}</span>
        </div>
        {!disabled && (
          <ItemControls index={flatIndex} items={items} onItems={onItems} noun="place" />
        )}
      </div>
      <div className="flex flex-col gap-2">{children}</div>
    </div>
  );
};

/**
 * Wrapper for a top-level block (a section, or a packages row). Hands the drag
 * handle back so each block can put it wherever its own header wants it.
 */
const SortableNodeShell = ({
  nodeIndex,
  disabled,
  className = '',
  children,
}: {
  nodeIndex: number;
  disabled?: boolean;
  className?: string;
  children: (handle: React.ReactNode) => React.ReactNode;
}): React.JSX.Element => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: nodeDragId(nodeIndex),
    disabled,
  });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`${className} ${isDragging ? 'z-10 opacity-60 shadow-md' : ''}`}
    >
      {children(
        disabled ? null : <DragHandle label="Reorder section" {...attributes} {...listeners} />,
      )}
    </div>
  );
};

/**
 * The places editor: a flat list, shown as collapsible sections.
 *
 * A guide runs to 50+ places and every one of them is an image, a rich body, a
 * details strip and two link fields. Flat, that is several screens of scrolling
 * to reach the beaches - so each heading collapses its own places, and the page
 * opens as a ten-row outline you can jump around in.
 *
 * The collapsing is presentation only. What's stored is still the single
 * ordered array of entries, which is why the section arrows can move a heading
 * and its places together while the arrows inside still move one place across a
 * boundary into the section above or below.
 */
const PlacesRepeater = ({
  items,
  onItems,
  disabled,
  renderPlace,
  blankPlace,
}: {
  items: Data[];
  onItems: (next: Data[]) => void;
  disabled?: boolean;
  renderPlace: (item: Data, set: (patch: Data) => void, index: number) => React.ReactNode;
  blankPlace: () => Data;
}): React.JSX.Element => {
  const nodes = React.useMemo(() => buildPlaceNodes(items), [items]);
  // Sections start closed - the point of the outline is that it's short. Keyed
  // by position, so a section stays open under you while you edit it.
  const [open, setOpen] = React.useState<Record<number, boolean>>({});
  const toggle = (n: number): void => setOpen((prev) => ({ ...prev, [n]: !prev[n] }));

  const setAt =
    (index: number) =>
    (patch: Data): void =>
      onItems(items.map((it, i) => (i === index ? { ...it, ...patch } : it)));

  const insertAt = (index: number, entry: Data): void => {
    const next = [...items];
    next.splice(index, 0, entry);
    onItems(next);
  };

  /** Swap two whole blocks, then flatten back to the stored array. */
  const moveNode = (from: number, to: number): void => {
    if (to < 0 || to >= nodes.length) return;
    const reordered = [...nodes];
    [reordered[from], reordered[to]] = [reordered[to]!, reordered[from]!];
    onItems(reordered.flatMap((n) => nodeItems(n, items)));
  };

  const placeNumber = (index: number): number =>
    items.slice(0, index + 1).filter(isPlaceRow).length;

  const placeCard = (index: number, dragId: string): React.JSX.Element => (
    <SortablePlaceCard
      key={dragId}
      dragId={dragId}
      flatIndex={index}
      label={`Place ${placeNumber(index)}`}
      items={items}
      onItems={onItems}
      disabled={disabled}
    >
      {renderPlace(items[index]!, setAt(index), index)}
    </SortablePlaceCard>
  );

  const sensors = useSensors(
    // A few pixels of travel before a drag starts, so clicking a section header
    // to open it doesn't register as a failed drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent): void => {
    const next = reorderPlaceEntries(
      items,
      String(event.active.id),
      event.over ? String(event.over.id) : null,
    );
    if (next) onItems(next);
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext
        items={nodes.map((_, n) => nodeDragId(n))}
        strategy={verticalListSortingStrategy}
      >
        <div className="flex flex-col gap-3">
          {nodes.map((node, n) => {
            if (node.kind === 'loose') {
              // Ungrouped: the place itself is the top-level row, so it drags
              // among the sections rather than inside one.
              return placeCard(node.index, nodeDragId(n));
            }

            if (node.kind === 'packages') {
              const item = items[node.index]!;
              return (
                <SortableNodeShell
                  key={`packages-${n}`}
                  nodeIndex={n}
                  disabled={disabled}
                  className="rounded-md border border-(--border) bg-(--z-50) p-3"
                >
                  {(handle) => (
                    <>
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <span className="flex min-w-0 items-center gap-1.5 text-xs font-medium text-(--fg-faint)">
                          {handle}
                          <Package className="h-3.5 w-3.5 shrink-0" /> Related packages
                        </span>
                        {!disabled && (
                          <div className="flex shrink-0 items-center gap-2">
                            <button
                              type="button"
                              className="text-(--fg-faint) hover:text-(--fg) disabled:cursor-not-allowed disabled:opacity-30"
                              disabled={n === 0}
                              onClick={() => moveNode(n, n - 1)}
                              aria-label="Move packages block up"
                              title="Move up"
                            >
                              <ChevronUp className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              className="text-(--fg-faint) hover:text-(--fg) disabled:cursor-not-allowed disabled:opacity-30"
                              disabled={n === nodes.length - 1}
                              onClick={() => moveNode(n, n + 1)}
                              aria-label="Move packages block down"
                              title="Move down"
                            >
                              <ChevronDown className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              className="text-(--fg-faint) hover:text-(--ds-error)"
                              onClick={() => onItems(items.filter((_, i) => i !== node.index))}
                              aria-label="Remove packages block"
                              title="Remove"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                      <Text
                        label="Heading (optional)"
                        value={item.name}
                        onChange={(v) => setAt(node.index)({ name: v })}
                        placeholder="Related Packages"
                        disabled={disabled}
                      />
                      <p className="mt-1 text-[12px] text-(--fg-faint)">
                        Shows the packages from this page’s Packages tab, right here in the list.
                        Add as many of these as you like between sections - once there’s one,
                        packages appear only where you put them.
                      </p>
                    </>
                  )}
                </SortableNodeShell>
              );
            }

            const heading = items[node.headingIndex]!;
            const count = node.placeIndexes.length;
            const isOpen = open[n] === true;
            return (
              <SortableNodeShell
                key={`group-${n}`}
                nodeIndex={n}
                disabled={disabled}
                className="rounded-md border border-(--border) bg-(--z-50)"
              >
                {(handle) => (
                  <>
                    <div className="flex items-center gap-2 px-3 py-2.5">
                      {handle}
                      <button
                        type="button"
                        onClick={() => toggle(n)}
                        aria-expanded={isOpen}
                        className="flex min-w-0 flex-1 items-center gap-2 text-left"
                      >
                        <ChevronRight
                          aria-hidden
                          className={`h-4 w-4 shrink-0 text-(--fg-faint) transition-transform ${
                            isOpen ? 'rotate-90' : ''
                          }`}
                        />
                        <span className="truncate text-sm font-semibold text-(--fg)">
                          {typeof heading.name === 'string' && heading.name.trim()
                            ? heading.name
                            : 'Untitled section'}
                        </span>
                        <span className="shrink-0 text-xs text-(--fg-faint)">
                          {count} {count === 1 ? 'place' : 'places'}
                        </span>
                      </button>
                      {!disabled && (
                        <div className="flex shrink-0 items-center gap-2">
                          <button
                            type="button"
                            className="text-(--fg-faint) hover:text-(--fg) disabled:cursor-not-allowed disabled:opacity-30"
                            disabled={n === 0}
                            onClick={() => moveNode(n, n - 1)}
                            aria-label="Move section up"
                            title="Move section up (with its places)"
                          >
                            <ChevronUp className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            className="text-(--fg-faint) hover:text-(--fg) disabled:cursor-not-allowed disabled:opacity-30"
                            disabled={n === nodes.length - 1}
                            onClick={() => moveNode(n, n + 1)}
                            aria-label="Move section down"
                            title="Move section down (with its places)"
                          >
                            <ChevronDown className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            className="text-(--fg-faint) hover:text-(--ds-error)"
                            onClick={() => onItems(items.filter((_, i) => i !== node.headingIndex))}
                            aria-label="Remove section heading"
                            title="Remove heading — its places join the section above"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </div>

                    {isOpen && (
                      <div className="flex flex-col gap-3 border-t border-(--border) p-3">
                        <div>
                          <Text
                            label="Section heading"
                            value={heading.name}
                            onChange={(v) => setAt(node.headingIndex)({ name: v })}
                            placeholder="Best Beaches to Visit"
                            disabled={disabled}
                          />
                          <p className="mt-1 text-[12px] text-(--fg-faint)">
                            Every place in this section belongs to it. Drag a place by its handle to
                            reorder it here; use its arrows to push it into the section above or
                            below.
                          </p>
                        </div>
                        {/* Places sort within their own section; crossing into
                            another one is what the arrows are for. */}
                        <SortableContext
                          items={node.placeIndexes.map((i) => placeDragId(i))}
                          strategy={verticalListSortingStrategy}
                        >
                          {node.placeIndexes.map((i) => placeCard(i, placeDragId(i)))}
                        </SortableContext>
                        {!disabled && (
                          <AddButton
                            label="Add place to this section"
                            onClick={() => insertAt(nodeEndIndex(node) + 1, blankPlace())}
                          />
                        )}
                      </div>
                    )}
                  </>
                )}
              </SortableNodeShell>
            );
          })}

          {!disabled && (
            <div className="flex flex-col gap-2 sm:flex-row">
              <AddButton
                label="Add place"
                className="flex-1"
                onClick={() => onItems([...items, blankPlace()])}
              />
              <AddButton
                label="Add section heading"
                className="flex-1"
                onClick={() => onItems([...items, { is_heading: true, name: '' }])}
              />
              <AddButton
                label="Add related packages"
                className="flex-1"
                onClick={() => onItems([...items, { is_packages: true, name: '' }])}
              />
            </div>
          )}
        </div>
      </SortableContext>
    </DndContext>
  );
};

/**
 * PLACES_GRID: "Places to visit" cards. Heading + supporting line come from
 * the section's title / subtitle fields; each card is an image + place name +
 * a rich description + optional link. The description is the same editor the
 * blog uses, so a place can be written up with headings, bullets and links, and
 * the card expands to the whole thing on "Read more".
 *
 * The list also holds group headings ("Best Cities to Visit", "Best Beaches to
 * Visit"), each owning the places under it until the next one, and inline
 * Related Packages blocks that can sit between sections. All three are entries
 * in the same ordered array, so one set of arrows orders the lot - and the
 * editor collapses each section, so a 50-place guide opens as a short outline
 * rather than several screens of cards.
 *
 * Card details are author-named label/value pairs, so a place carries only the
 * meta it actually has. The Read More button renders only when the card has a
 * link - no link, no button on the site.
 */
const PlacesGrid = ({ data, onChange, disabled }: FormProps): React.JSX.Element => (
  <>
    <HeadingHint />
    {/* No cap: a destination guide can legitimately run to 50+ places. */}
    <PlacesRepeater
      items={arr(data.items)}
      onItems={(items) => onChange({ ...data, items })}
      blankPlace={() => ({
        image_media_id: '',
        name: '',
        description: '',
        description_html: '',
        href: '',
        meta: [],
        best_time: '',
        timings: '',
        entry_fee: '',
        cta_label: 'Read More',
      })}
      disabled={disabled}
      renderPlace={(item, set) => (
        <>
          <MediaDropField
            label="Image"
            kind="image"
            value={item.image_media_id as string}
            onChange={(id) => set({ image_media_id: id })}
            disabled={disabled}
          />
          <Text
            label="Place name"
            value={item.name}
            onChange={(v) => set({ name: v })}
            placeholder="Uluwatu Temple"
            disabled={disabled}
          />
          <div>
            <DeferredRichText
              label="Description"
              value={placeBodyHtml(item)}
              onChange={(html) =>
                // The plain mirror keeps pre-editor renderers (and anything that
                // needs text, not markup) working, and clearing the editor has
                // to clear it too - otherwise the site keeps showing the old
                // plain copy as a fallback.
                set({ description_html: html, description: htmlToPlainText(html).slice(0, 600) })
              }
              placeholder="Write the place up — headings, bullets, links, whatever this one needs…"
              folder="website-pages"
              disabled={disabled}
            />
            <p className="mt-1 text-[12px] text-(--fg-faint)">
              The card shows the opening lines and expands to the full write-up on “Read more”.
              Every place can have its own structure — there’s no set of sections to fill in.
            </p>
          </div>
          <PlaceMetaEditor
            rows={placeMetaRows(item)}
            // Writing the pairs retires the three legacy fields on this card, so
            // the renderer's fallback can never resurrect a stale value.
            onRows={(meta) => set({ meta, best_time: '', timings: '', entry_fee: '' })}
            disabled={disabled}
          />
          <Text
            label="Link - the page or blog Read More opens (leave blank to hide the button)"
            value={item.href}
            onChange={(v) => set({ href: v })}
            placeholder="/international-tour-holidays/france"
            disabled={disabled}
          />
          <Text
            label="CTA label (only shown when a link is set)"
            value={item.cta_label}
            onChange={(v) => set({ cta_label: v })}
            placeholder="Read More"
            disabled={disabled}
          />
        </>
      )}
    />
  </>
);

/**
 * EVENTS_GRID: "Discover Events" cards. Heading + supporting line come from the
 * section's title / subtitle fields; each event is an image + name + description
 * + a Key Capabilities bullet list (one bullet per line).
 */
const EventsGrid = ({ data, onChange, disabled }: FormProps): React.JSX.Element => (
  <>
    <HeadingHint />
    <Text
      label="Eyebrow (guide pages only)"
      value={data.eyebrow}
      onChange={(v) => onChange({ ...data, eyebrow: v })}
      placeholder="Cultural calendar"
      disabled={disabled}
    />
    <Text
      label="Capabilities heading"
      value={data.capabilities_heading}
      onChange={(v) => onChange({ ...data, capabilities_heading: v })}
      placeholder="Key Capabilities"
      disabled={disabled}
    />
    <Repeater
      items={arr(data.items)}
      onItems={(items) => onChange({ ...data, items })}
      blank={() => ({
        image_media_id: '',
        name: '',
        when: '',
        description: '',
        capabilities: [],
        href: '',
      })}
      disabled={disabled}
      render={(item, set) => (
        <>
          <MediaDropField
            label="Image"
            kind="image"
            value={item.image_media_id as string}
            onChange={(id) => set({ image_media_id: id })}
            disabled={disabled}
          />
          <Text
            label="Event name"
            value={item.name}
            onChange={(v) => set({ name: v })}
            placeholder="La Tomatina, Spain"
            disabled={disabled}
          />
          <Text
            label="When (guide pages only)"
            value={item.when}
            onChange={(v) => set({ when: v })}
            placeholder="June - July"
            disabled={disabled}
          />
          <Area
            label="Description"
            value={item.description}
            onChange={(v) => set({ description: v })}
            disabled={disabled}
          />
          <Area
            label="Key capabilities (one per line)"
            value={linesToText(item.capabilities)}
            onChange={(v) => set({ capabilities: textToLines(v) })}
            disabled={disabled}
          />
          <Text
            label="Link (optional)"
            value={item.href}
            onChange={(v) => set({ href: v })}
            disabled={disabled}
          />
        </>
      )}
    />
  </>
);

/**
 * ACTIVITIES_GRID: "Your People. Your Place." travel-style bubbles. Heading +
 * supporting line come from the section's title / subtitle fields; each bubble
 * is a circular image + label + link.
 */
const ActivitiesGrid = ({ data, onChange, disabled }: FormProps): React.JSX.Element => (
  <>
    <HeadingHint />
    <Repeater
      items={arr(data.items)}
      onItems={(items) => onChange({ ...data, items })}
      blank={() => ({ image_media_id: '', label: '', href: '/' })}
      disabled={disabled}
      render={(item, set) => (
        <>
          <MediaDropField
            label="Image"
            kind="image"
            value={item.image_media_id as string}
            onChange={(id) => set({ image_media_id: id })}
            disabled={disabled}
          />
          <Text
            label="Label"
            value={item.label}
            onChange={(v) => set({ label: v })}
            placeholder="Family"
            disabled={disabled}
          />
          <Text
            label="Link"
            value={item.href}
            onChange={(v) => set({ href: v })}
            placeholder="/"
            disabled={disabled}
          />
        </>
      )}
    />
  </>
);

/**
 * PACKAGE_CAROUSELS: theme-tagged package carousels (the "Sliders" model).
 * Each row picks a Themes & Collections entry; the website renders that
 * collection's packages. Heading / supporting line come from the section's
 * title / subtitle fields.
 */
const PackageCarousels = ({ data, onChange, disabled }: FormProps): React.JSX.Element => {
  const tagsQuery = useMarketingTagsList({
    page: 1,
    pageSize: 200,
    sortDir: 'asc' as const,
    type: 'collection',
    isActive: true,
  });
  const options = (tagsQuery.data?.data ?? []).map((t) => ({ value: t.id, label: t.name }));
  const tagName = (id: string): string => options.find((o) => o.value === id)?.label ?? '';

  const destQuery = useDestinationsList({
    page: 1,
    pageSize: 200,
    sortBy: 'name',
    sortDir: 'asc',
  });
  const destOptions = [
    { value: '', label: '- None (use collection) -' },
    ...(destQuery.data?.data ?? []).map((d) => ({ value: d.id, label: d.name })),
  ];
  const destName = (id: string): string => destOptions.find((o) => o.value === id)?.label ?? '';

  return (
    <>
      <HeadingHint />
      <p className="text-[12px] text-(--fg-faint)">
        Each row is a package carousel. Pick a <strong>Destination</strong> to show packages tagged
        to it (e.g. Europe), or a <strong>Collection</strong> to show packages tagged with that
        collection. Destination wins when both are set.
      </p>
      <Repeater
        items={arr(data.groups)}
        onItems={(groups) => onChange({ ...data, groups })}
        blank={() => ({
          theme_tag_id: '',
          destination_id: '',
          heading: '',
          subheading: '',
          sort_order: 'newest',
          limit: 12,
        })}
        disabled={disabled}
        render={(item, set, index) => (
          <>
            <UzCombobox
              name={`pkgcar-dest-${index}`}
              label="Destination (packages tagged to it)"
              placeholder="Select a destination…"
              searchPlaceholder="Search destinations…"
              value={(item.destination_id as string) ?? ''}
              onValueChange={(v) => {
                const patch: Data = { destination_id: v };
                if (v && !((item.heading as string) ?? '').trim()) patch.heading = destName(v);
                set(patch);
              }}
              options={destOptions}
              loading={destQuery.isLoading}
              disabled={disabled}
            />
            <UzCombobox
              name={`pkgcar-theme-${index}`}
              label="Collection (used only if no destination)"
              placeholder="Select a collection…"
              searchPlaceholder="Search collections…"
              value={(item.theme_tag_id as string) ?? ''}
              onValueChange={(v) => {
                const patch: Data = { theme_tag_id: v };
                if (!((item.heading as string) ?? '').trim()) patch.heading = tagName(v);
                set(patch);
              }}
              options={options}
              loading={tagsQuery.isLoading}
              disabled={disabled}
            />
            <Text
              label="Carousel heading"
              value={item.heading}
              onChange={(v) => set({ heading: v })}
              placeholder="Discover Europe"
              disabled={disabled}
            />
            <Text
              label="Carousel subheading"
              value={item.subheading}
              onChange={(v) => set({ subheading: v })}
              placeholder="Explore the world, stress-free."
              disabled={disabled}
            />
            <Choice
              label="Sort order"
              value={
                item.sort_order === 'price_low' || item.sort_order === 'price_high'
                  ? (item.sort_order as 'price_low' | 'price_high')
                  : 'newest'
              }
              options={[
                { value: 'newest', label: 'Newest' },
                { value: 'price_low', label: 'Price: low to high' },
                { value: 'price_high', label: 'Price: high to low' },
              ]}
              onChange={(v) => set({ sort_order: v })}
              disabled={disabled}
            />
            <Num
              label="Max cards (1-24)"
              value={typeof item.limit === 'number' ? (item.limit as number) : 12}
              min={1}
              max={24}
              onChange={(v) => set({ limit: v })}
              disabled={disabled}
            />
          </>
        )}
      />
    </>
  );
};

/**
 * VISION_MISSION: two-column Vision / Mission statement card (About Us). Each
 * column is a short heading + a paragraph.
 */
const VisionMission = ({ data, onChange, disabled }: FormProps): React.JSX.Element => (
  <>
    <HeadingHint />
    <Text
      label="Vision heading"
      value={data.vision_title}
      onChange={(v) => onChange({ ...data, vision_title: v })}
      placeholder="Vision"
      disabled={disabled}
    />
    <Area
      label="Vision text"
      value={data.vision_body}
      onChange={(v) => onChange({ ...data, vision_body: v })}
      disabled={disabled}
    />
    <Text
      label="Mission heading"
      value={data.mission_title}
      onChange={(v) => onChange({ ...data, mission_title: v })}
      placeholder="Mission"
      disabled={disabled}
    />
    <Area
      label="Mission text"
      value={data.mission_body}
      onChange={(v) => onChange({ ...data, mission_body: v })}
      disabled={disabled}
    />
  </>
);

/**
 * CELEBRATION: anniversary / milestone block (About Us "15 Years…"). Heading +
 * description come from the section's title / subtitle fields; the images are a
 * horizontal carousel.
 */
const Celebration = ({ data, onChange, disabled }: FormProps): React.JSX.Element => {
  const ids = (data.photo_media_ids as string[] | undefined) ?? [];
  return (
    <>
      <HeadingHint />
      <MediaGalleryField
        label="Photos"
        value={ids}
        onChange={(next) => onChange({ ...data, photo_media_ids: next })}
        disabled={disabled}
      />
    </>
  );
};

/**
 * CALLBACK_CHAT: compact "Ready to Plan your Trip?" lead form with a "Request a
 * callback" submit and a "Chat With Us" WhatsApp button. Heading / subheading
 * come from the section's title / subtitle fields.
 */
const CallbackChat = ({ data, onChange, disabled }: FormProps): React.JSX.Element => {
  const bool = (k: string): boolean => data[k] !== false;
  const destinationsQuery = useDestinationsList({
    page: 1,
    pageSize: 200,
    sortBy: 'name',
    sortDir: 'asc',
  });
  const destOptions = (destinationsQuery.data?.data ?? []).map((d) => ({
    value: d.id,
    label: d.name,
  }));
  const destId = typeof data.destination_id === 'string' ? data.destination_id : '';

  return (
    <>
      <HeadingHint />
      <Text
        label="Lead source code"
        value={data.query_source_code}
        onChange={(v) => onChange({ ...data, query_source_code: v })}
        placeholder="website-callback"
        disabled={disabled}
      />
      <p className="text-xs text-zinc-500">
        Must match an active Query Source code (e.g. website-callback). Unknown codes make the
        public form fail with &quot;Something went wrong&quot;.
      </p>
      <UzCombobox
        name="callback-chat-destination"
        label="Default destination"
        placeholder="Optional - pre-fills the form Destination field"
        searchPlaceholder="Search destinations…"
        value={destId}
        onValueChange={(v) => onChange({ ...data, destination_id: v })}
        options={destOptions}
        loading={destinationsQuery.isLoading}
        disabled={disabled}
      />
      <p className="text-xs text-zinc-500">
        Pre-fills Destination on the website form (visitor can still change it). On landing pages,
        the packages section destination is used when this is blank.
      </p>
      <Text
        label="WhatsApp / chat link"
        value={data.whatsapp_href}
        onChange={(v) => onChange({ ...data, whatsapp_href: v })}
        placeholder="Uses the WhatsApp number from Settings"
        disabled={disabled}
      />
      <p className="text-xs text-zinc-500">
        Leave blank to use the company WhatsApp number from Settings &rarr; Website. Only set this
        to point this one section at a different line.
      </p>
      <Text
        label="Callback button label"
        value={data.callback_label}
        onChange={(v) => onChange({ ...data, callback_label: v })}
        placeholder="Request a callback"
        disabled={disabled}
      />
      <Text
        label="Chat button label"
        value={data.chat_label}
        onChange={(v) => onChange({ ...data, chat_label: v })}
        placeholder="Chat With Us"
        disabled={disabled}
      />

      {/* Two forms, two columns: mid-page card on the left, closing strip on
          the right. Stacks on narrow screens. */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:items-start">
        <div className="flex flex-col gap-2">
          <div className="mt-1 text-xs font-semibold tracking-wide text-(--fg-muted) uppercase">
            Mid-page form
          </div>
          <Toggle
            label="Show form"
            hint="Off hides the whole mid-page form card."
            checked={bool('card_show_form')}
            onChange={(v) => onChange({ ...data, card_show_form: v })}
            disabled={disabled}
          />
          {bool('card_show_form') && (
            <>
              <Toggle
                label="Show callback button"
                checked={bool('card_show_callback')}
                onChange={(v) => onChange({ ...data, card_show_callback: v })}
                disabled={disabled}
              />
              <Toggle
                label="Show chat button"
                checked={bool('card_show_chat')}
                onChange={(v) => onChange({ ...data, card_show_chat: v })}
                disabled={disabled}
              />
            </>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <div className="mt-1 text-xs font-semibold tracking-wide text-(--fg-muted) uppercase">
            Closing form (below FAQ)
          </div>
          <Toggle
            label="Show form"
            hint="Off hides the full-width closing form at the bottom of the page."
            checked={bool('strip_show_form')}
            onChange={(v) => onChange({ ...data, strip_show_form: v })}
            disabled={disabled}
          />
          {bool('strip_show_form') && (
            <>
              <Toggle
                label="Show callback button"
                checked={bool('strip_show_callback')}
                onChange={(v) => onChange({ ...data, strip_show_callback: v })}
                disabled={disabled}
              />
              <Toggle
                label="Show chat button"
                checked={bool('strip_show_chat')}
                onChange={(v) => onChange({ ...data, strip_show_chat: v })}
                disabled={disabled}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
};

/**
 * LANDING_PACKAGES: hand-written package cards for landing pages. Nothing is
 * read from the package master - the editor types every field, so a campaign
 * card can advertise an offer that has no package row behind it and a package
 * edit never rewrites a live ad page. Heading / subheading come from the
 * section's title / subtitle fields.
 */
const LandingPackages = ({ data, onChange, disabled }: FormProps): React.JSX.Element => {
  const destinationsQuery = useDestinationsList({
    page: 1,
    pageSize: 200,
    sortBy: 'name',
    sortDir: 'asc',
  });
  const destId = typeof data.destination_id === 'string' ? data.destination_id : '';
  const destOptions = (destinationsQuery.data?.data ?? []).map((d) => ({
    value: d.id,
    label: d.name,
  }));

  return (
    <>
      <HeadingHint />
      <UzCombobox
        name="lp-destination"
        label="Campaign destination"
        placeholder="Select a destination…"
        searchPlaceholder="Search destinations…"
        value={destId}
        onValueChange={(v) => onChange({ ...data, destination_id: v })}
        options={destOptions}
        loading={destinationsQuery.isLoading}
        disabled={disabled}
      />
      <p className="text-xs text-zinc-500">
        Not shown on the cards. It is the destination filed against this page&apos;s callback leads
        in Lead Parsing.
      </p>

      <div>
        <span className="block text-xs font-medium text-(--fg-muted)">Cards</span>
        <p className="mt-0.5 text-[12px] text-(--fg-faint)">
          Every field is typed by hand - these cards are not linked to the Packages master.
        </p>
      </div>
      <Repeater
        items={arr(data.items)}
        onItems={(items) => onChange({ ...data, items })}
        blank={() => ({
          duration_label: '',
          name: '',
          location: '',
          inclusion_ids: [],
          highlights: [],
          price: '',
          strike_price: '',
          image_media_ids: [],
          image_urls: [],
        })}
        disabled={disabled}
        render={(item, set) => (
          <>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Text
                label="Duration"
                value={item.duration_label}
                onChange={(v) => set({ duration_label: v })}
                placeholder="6N/7D"
                disabled={disabled}
              />
              <Text
                label="Destination line"
                value={item.location}
                onChange={(v) => set({ location: v })}
                placeholder="Vietnam"
                disabled={disabled}
              />
            </div>
            <Text
              label="Package name"
              value={item.name}
              onChange={(v) => set({ name: v })}
              placeholder="Romantic Vietnam Odyssey: 7 Days / 6 Nights Honeymoon Escape"
              disabled={disabled}
            />
            <HeroInclusionChecklist
              label="Inclusions"
              value={(item.inclusion_ids as string[] | undefined) ?? []}
              onChange={(ids) => set({ inclusion_ids: ids })}
              disabled={disabled}
            />
            <Area
              label="Highlights (one bullet per line)"
              value={linesToText(item.highlights)}
              onChange={(v) => set({ highlights: textToLines(v) })}
              disabled={disabled}
            />
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Text
                label="Price"
                value={item.price}
                onChange={(v) => set({ price: v })}
                placeholder="53999"
                disabled={disabled}
              />
              <Text
                label="Strike price (optional)"
                value={item.strike_price}
                onChange={(v) => set({ strike_price: v })}
                placeholder="64999"
                disabled={disabled}
              />
            </div>
            <p className="-mt-1 text-[12px] text-(--fg-faint)">
              Digits only are printed as ₹53,999. Anything else shows exactly as typed.
            </p>
            <MediaGalleryField
              label="Images (upload or pick from the library)"
              value={(item.image_media_ids as string[] | undefined) ?? []}
              onChange={(next) => set({ image_media_ids: next })}
              disabled={disabled}
            />
            <Area
              label="Or paste image URLs (one per line)"
              value={linesToText(item.image_urls)}
              onChange={(v) => set({ image_urls: textToLines(v) })}
              disabled={disabled}
            />
            <p className="-mt-1 text-[12px] text-(--fg-faint)">
              Shown after the uploaded images, in the same carousel.
            </p>
          </>
        )}
      />

      <Text
        label="CTA label"
        value={data.cta_label}
        onChange={(v) => onChange({ ...data, cta_label: v })}
        placeholder="Customize Package"
        disabled={disabled}
      />
      <Text
        label="Lead source code"
        value={data.query_source_code}
        onChange={(v) => onChange({ ...data, query_source_code: v })}
        placeholder="website-customize-package"
        disabled={disabled}
      />
      <p className="text-xs text-zinc-500">
        Filed against leads from the Customize Package CTA (it opens the site enquiry popup, but
        records this source instead of the popup&apos;s). Must match an active Query Source code;
        unknown codes make the public form fail with &quot;Something went wrong&quot;.
      </p>
      <Text
        label="Price note"
        value={data.price_note}
        onChange={(v) => onChange({ ...data, price_note: v })}
        placeholder="*Price Varies By Travel Month & Preferences"
        disabled={disabled}
      />
      <Text
        label="Footnote"
        value={data.footnote}
        onChange={(v) => onChange({ ...data, footnote: v })}
        placeholder="Customized by 32 Travelers"
        disabled={disabled}
      />
    </>
  );
};

// ── Reviews page sections (2026-09-11) ──────────────────────────────────────

/**
 * AWARDS_STATS: award badge on the left + a card of icon stat tiles on the
 * right. A tile's figure can be fed live from Google (rating / review count)
 * with the typed figure as the fallback when the Google key is not set.
 */
const AwardsStatsForm = ({ data, onChange, disabled }: FormProps): React.JSX.Element => (
  <>
    <HeadingHint />
    <MediaDropField
      label="Award badge (left side)"
      kind="image"
      value={data.badge_media_id as string}
      onChange={(id) => onChange({ ...data, badge_media_id: id })}
      disabled={disabled}
    />
    <Text
      label="Badge alt text"
      value={data.badge_alt}
      onChange={(v) => onChange({ ...data, badge_alt: v })}
      placeholder="India's Fastest Growing Travel Brand"
      disabled={disabled}
    />
    <p className="text-[12px] text-(--fg-faint)">
      Tiles render left → right in one card. “Google rating” / “Google review count” tiles show the
      live figure once the Google Places key + Place ID are set in Secrets Manager; until then the
      typed figure is shown.
    </p>
    <Repeater
      items={arr(data.items)}
      onItems={(items) => onChange({ ...data, items })}
      blank={() => ({ value: '', label: '', icon: '', icon_media_id: '', source: 'manual' })}
      disabled={disabled}
      render={(item, set) => (
        <>
          <Choice
            label="Figure source"
            value={(typeof item.source === 'string' ? item.source : 'manual') as string}
            options={[
              { value: 'manual', label: 'Typed' },
              { value: 'google_rating', label: 'Google rating' },
              { value: 'google_review_count', label: 'Google review count' },
            ]}
            onChange={(v) => set({ source: v })}
            disabled={disabled}
          />
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Text
              label={item.source === 'manual' || !item.source ? 'Figure' : 'Figure (fallback)'}
              value={item.value}
              onChange={(v) => set({ value: v })}
              placeholder="53k+"
              disabled={disabled}
            />
            <Text
              label="Caption"
              value={item.label}
              onChange={(v) => set({ label: v })}
              placeholder="(Followers)"
              disabled={disabled}
            />
          </div>
          <IconPicker
            label="Icon (google / facebook / instagram / award …)"
            value={item.icon}
            onChange={(v) => set({ icon: v })}
            disabled={disabled}
          />
          <MediaDropField
            label="Icon image (optional - overrides the icon)"
            kind="image"
            variant="square"
            value={item.icon_media_id as string}
            onChange={(id) => set({ icon_media_id: id })}
            disabled={disabled}
          />
        </>
      )}
    />
  </>
);

/**
 * VIDEO_REVIEWS: filterable grid of vertical video-review cards. Cards take the
 * same inputs as Creator reels (reel link or upload, cover, tag, CTA); the tag
 * chip doubles as the destination filter pill above the grid.
 */
const VideoReviewsForm = ({ data, onChange, disabled }: FormProps): React.JSX.Element => (
  <>
    <HeadingHint />
    <Toggle
      label="Show destination filter pills"
      hint="Built from each card's tag chip (e.g. Vietnam, Singapore). Cards without a tag show under every filter."
      checked={data.show_filters !== false}
      onChange={(v) => onChange({ ...data, show_filters: v })}
      disabled={disabled}
    />
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
      <Num
        label="Cards shown first"
        value={typeof data.initial_count === 'number' ? data.initial_count : 6}
        min={1}
        max={30}
        onChange={(v) => onChange({ ...data, initial_count: Number.isFinite(v) && v > 0 ? v : 6 })}
        disabled={disabled}
      />
      <Num
        label="Cards per “Load more”"
        value={typeof data.load_more_step === 'number' ? data.load_more_step : 6}
        min={1}
        max={30}
        onChange={(v) => onChange({ ...data, load_more_step: Number.isFinite(v) && v > 0 ? v : 6 })}
        disabled={disabled}
      />
      <Text
        label="Load more label"
        value={data.load_more_label}
        onChange={(v) => onChange({ ...data, load_more_label: v })}
        placeholder="Load more reviews"
        disabled={disabled}
      />
    </div>
    <Repeater
      items={arr(data.items)}
      onItems={(items) => onChange({ ...data, items })}
      blank={() => ({
        reel_url: '',
        video_media_id: '',
        poster_media_id: '',
        caption: '',
        tag: '',
        cta_label: '',
        cta_href: '',
      })}
      disabled={disabled}
      render={(item, set) => <ReelsStripRow item={item} set={set} disabled={disabled} />}
    />
  </>
);

/**
 * TRAVELLER_REVIEWS: stacked review cards (avatar, name, date, rating, booked
 * package, review, trip photos) merged with the live Google reviews.
 */
const TravellerReviewsForm = ({ data, onChange, disabled }: FormProps): React.JSX.Element => (
  <>
    <HeadingHint />
    <Toggle
      label="Show reviewer photos"
      hint="Round reviewer avatar on each card. Off = no photo and no grey placeholder circle (typed + Google reviews)."
      checked={data.show_avatars !== false}
      onChange={(v) => onChange({ ...data, show_avatars: v })}
      disabled={disabled}
    />
    <Toggle
      label="Merge live Google reviews"
      hint="Needs the Google Places key + Place ID in Secrets Manager. Google returns at most 5 reviews (name, rating, text, date - no trip photos)."
      checked={data.show_google_reviews !== false}
      onChange={(v) => onChange({ ...data, show_google_reviews: v })}
      disabled={disabled}
    />
    {data.show_google_reviews !== false && (
      <Choice
        label="Google reviews position"
        value={
          (typeof data.google_reviews_position === 'string'
            ? data.google_reviews_position
            : 'bottom') as string
        }
        options={[
          { value: 'top', label: 'Before typed reviews' },
          { value: 'bottom', label: 'After typed reviews' },
        ]}
        onChange={(v) => onChange({ ...data, google_reviews_position: v })}
        disabled={disabled}
      />
    )}
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
      <Num
        label="Reviews shown first"
        value={typeof data.initial_count === 'number' ? data.initial_count : 4}
        min={1}
        max={50}
        onChange={(v) => onChange({ ...data, initial_count: Number.isFinite(v) && v > 0 ? v : 4 })}
        disabled={disabled}
      />
      <Num
        label="Reviews per “Load more”"
        value={typeof data.load_more_step === 'number' ? data.load_more_step : 4}
        min={1}
        max={50}
        onChange={(v) => onChange({ ...data, load_more_step: Number.isFinite(v) && v > 0 ? v : 4 })}
        disabled={disabled}
      />
      <Text
        label="Load more label"
        value={data.load_more_label}
        onChange={(v) => onChange({ ...data, load_more_label: v })}
        placeholder="Load more reviews"
        disabled={disabled}
      />
    </div>
    <Repeater
      items={arr(data.items)}
      onItems={(items) => onChange({ ...data, items })}
      blank={() => ({
        photo_media_id: '',
        reviewer_name: '',
        date_label: '',
        rating: 5,
        booked_label: '',
        booked_href: '',
        quote: '',
        gallery_media_ids: [],
      })}
      disabled={disabled}
      render={(item, set) => (
        <>
          <MediaDropField
            label="Reviewer avatar"
            kind="image"
            variant="square"
            value={item.photo_media_id as string}
            onChange={(id) => set({ photo_media_id: id })}
            disabled={disabled}
          />
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Text
              label="Reviewer name"
              value={item.reviewer_name}
              onChange={(v) => set({ reviewer_name: v })}
              placeholder="Palak Jain"
              disabled={disabled}
            />
            <Text
              label="Reviewed on"
              value={item.date_label}
              onChange={(v) => set({ date_label: v })}
              placeholder="07 Jun 2026"
              disabled={disabled}
            />
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Text
              label="Rating (0-5)"
              value={typeof item.rating === 'number' ? String(item.rating) : '5'}
              onChange={(v) => {
                const n = Number(v);
                set({ rating: v === '' ? 5 : Math.max(0, Math.min(5, Number.isNaN(n) ? 5 : n)) });
              }}
              placeholder="5"
              disabled={disabled}
            />
            <Text
              label="Booked package"
              value={item.booked_label}
              onChange={(v) => set({ booked_label: v })}
              placeholder="Bangkok Pattaya Group Getaway"
              disabled={disabled}
            />
          </div>
          <Text
            label="Booked package link (optional)"
            value={item.booked_href}
            onChange={(v) => set({ booked_href: v })}
            placeholder="/thailand-tour-packages/bangkok-pattaya-5-days"
            disabled={disabled}
          />
          <Area
            label="Review"
            value={item.quote}
            onChange={(v) => set({ quote: v })}
            disabled={disabled}
          />
          <MediaGalleryField
            label="Trip photos (first 4 show inline, the rest behind “View All”)"
            value={(item.gallery_media_ids as string[] | undefined) ?? []}
            onChange={(next) => set({ gallery_media_ids: next.slice(0, 30) })}
            disabled={disabled}
          />
        </>
      )}
    />
  </>
);

// ── Funnel landing pages (/hotels, /flights, /activities) ───────────────────
//
// The first three are LIVE sections: the form stores a *selection* (which
// hotels, cities or routes; how many; for what stay) and the website fills in
// every name, photo, star rating and price from TBO. So there is no field here
// for a price, a discount or a rating - none of them is the editor's to type.

const LIVE_MODE_HINT =
  'Trending ranks by what visitors are doing on the website; the picks below top the row up when traffic is thin.';

const str = (v: unknown): string => (typeof v === 'string' ? v : '');

/** Stay-pricing knobs shared by the two hotel sections. */
const StayDatesFields = ({ data, onChange, disabled }: FormProps): React.JSX.Element => (
  <div className="grid grid-cols-2 gap-2">
    <div className="col-span-2">
      <Choice
        label="Check-in date"
        value={data.checkin_mode === 'friday' ? 'friday' : 'exact'}
        options={[
          { value: 'exact', label: 'Exactly that day' },
          { value: 'friday', label: 'Next Friday after it' },
        ]}
        onChange={(v) => onChange({ ...data, checkin_mode: v })}
        hint={
          data.checkin_mode === 'friday'
            ? 'The first Friday on or after today + the days below - a weekend stay. The date holds for a week, so prices refresh weekly.'
            : 'Today + the days below, exactly (1 = tomorrow). The date moves every day, so prices refresh daily.'
        }
        disabled={disabled}
      />
    </div>
    <Num
      label="Check-in, days from today"
      value={data.lead_days ?? 14}
      onChange={(v) => onChange({ ...data, lead_days: v })}
      min={1}
      max={120}
      disabled={disabled}
    />
    <Num
      label="Nights"
      value={data.nights ?? 1}
      onChange={(v) => onChange({ ...data, nights: v })}
      min={1}
      max={7}
      disabled={disabled}
    />
  </div>
);

/**
 * STAY_COLLECTION: a row of live hotel cards. Three ways to fill it - the
 * editor's picks, what is trending on the website, or the top-rated properties
 * of one city.
 */
const StayCollectionForm = ({ data, onChange, disabled }: FormProps): React.JSX.Element => {
  const mode = str(data.mode) || 'manual';
  const picks = arr(data.items);
  return (
    <>
      <HeadingHint />
      <Choice
        label="Fill this row with"
        value={mode}
        options={[
          { value: 'manual', label: 'My picks' },
          { value: 'trending', label: 'Trending' },
          { value: 'top_rated', label: 'Top rated in a city' },
        ]}
        onChange={(v) => onChange({ ...data, mode: v })}
        hint={
          mode === 'trending'
            ? 'Hotels visitors took to checkout most. ' + LIVE_MODE_HINT
            : mode === 'top_rated'
              ? 'The highest-starred hotels TBO lists for the city below. Your picks top the row up.'
              : 'Exactly the hotels you pick below, in this order.'
        }
        disabled={disabled}
      />

      {mode !== 'manual' && (
        <>
          <RemoteLookup
            label={mode === 'top_rated' ? 'City (required)' : 'Limit to a city (optional)'}
            placeholder="Search a city…"
            path="/stays/cities"
            toOption={(r) =>
              r.type === 'city' && typeof r.cityId === 'string'
                ? {
                    id: r.cityId,
                    label: [str(r.name), str(r.countryName)].filter(Boolean).join(', '),
                    description: `${typeof r.hotelCount === 'number' ? r.hotelCount : 0} hotels synced from TBO`,
                    raw: r,
                  }
                : null
            }
            onPick={(o) => onChange({ ...data, city_id: o.id, city_name: o.label })}
            disabled={disabled}
          />
          {str(data.city_id) ? (
            <div className="flex items-center justify-between rounded-md border border-(--border) bg-(--z-50) px-3 py-2 text-sm">
              <span>{str(data.city_name) || str(data.city_id)}</span>
              {!disabled && (
                <button
                  type="button"
                  className="text-[12px] text-(--fg-faint) hover:text-(--fg)"
                  onClick={() => onChange({ ...data, city_id: null, city_name: '' })}
                >
                  Clear
                </button>
              )}
            </div>
          ) : (
            mode === 'top_rated' && (
              <p className="text-[12px] text-(--fg-faint)">
                Pick a city - without one this row has nothing to rank and stays hidden.
              </p>
            )
          )}
        </>
      )}

      {mode === 'top_rated' && (
        <div className="grid grid-cols-2 gap-2">
          <Num
            label="Minimum stars"
            value={data.min_star ?? 4}
            onChange={(v) => onChange({ ...data, min_star: v })}
            min={3}
            max={5}
            disabled={disabled}
          />
          <Text
            label="Facility keyword (optional)"
            value={data.amenity_keyword}
            onChange={(v) => onChange({ ...data, amenity_keyword: v })}
            placeholder="pool, kids, spa, beach…"
            disabled={disabled}
          />
        </div>
      )}
      {mode === 'trending' && (
        <Num
          label="Look back, days"
          value={data.trending_days ?? 14}
          onChange={(v) => onChange({ ...data, trending_days: v })}
          min={1}
          max={90}
          disabled={disabled}
        />
      )}

      <RemoteLookup
        label={mode === 'manual' ? 'Add a hotel' : 'Add a top-up hotel'}
        placeholder="Search a hotel by name…"
        hint="Only hotels synced from TBO appear. Sync the city under TBO → Hotels if one is missing."
        path="/stays/cities"
        toOption={(r) =>
          r.type === 'hotel' && typeof r.hotelCode === 'string'
            ? {
                id: r.hotelCode,
                label: str(r.name),
                description: [
                  typeof r.starRating === 'number' ? `${r.starRating}★` : '',
                  str(r.cityName),
                  str(r.countryName),
                ]
                  .filter(Boolean)
                  .join(' · '),
                raw: r,
              }
            : null
        }
        onPick={(o) => {
          if (picks.some((p) => p.hotel_code === o.id) || picks.length >= 24) return;
          onChange({
            ...data,
            items: [
              ...picks,
              { hotel_code: o.id, hotel_name: o.label, city_name: str(o.raw.cityName) },
            ],
          });
        }}
        disabled={disabled}
      />
      <Repeater
        items={picks}
        onItems={(items) => onChange({ ...data, items })}
        blank={() => ({ hotel_code: '', hotel_name: '', city_name: '' })}
        // Rows come from the search above, never from "Add item": a hand-typed
        // hotel code that TBO does not know is a card that silently never shows.
        max={0}
        disabled={disabled}
        render={(item) => (
          <div className="text-sm">
            <div className="font-medium text-(--fg)">{str(item.hotel_name) || 'Unnamed hotel'}</div>
            <div className="text-[12px] text-(--fg-faint)">
              {[str(item.city_name), `TBO ${str(item.hotel_code)}`].filter(Boolean).join(' · ')}
            </div>
          </div>
        )}
      />

      <div className="grid grid-cols-3 gap-2">
        <Num
          label="Cards shown"
          value={data.max_cards ?? 6}
          onChange={(v) => onChange({ ...data, max_cards: v })}
          min={3}
          max={12}
          disabled={disabled}
        />
        <div className="col-span-2">
          <StayDatesFields data={data} onChange={onChange} disabled={disabled} />
        </div>
      </div>
      <p className="text-[12px] text-(--fg-faint)">
        Prices are live from TBO for one room, two adults, for the check-in date above. Hotels TBO
        has a price for come first; if there are fewer than “Cards shown”, the rest of the row is
        filled with hotels that say “Pick dates for a live price”. With fewer than three hotels in
        all, the row hides itself.
      </p>
      <div className="grid grid-cols-2 gap-2">
        <Text
          label="View-all label (optional)"
          value={data.view_all_label}
          onChange={(v) => onChange({ ...data, view_all_label: v })}
          placeholder="View all"
          disabled={disabled}
        />
        <Text
          label="View-all link (optional)"
          value={data.view_all_href}
          onChange={(v) => onChange({ ...data, view_all_href: v })}
          placeholder="Defaults to a search of the city"
          disabled={disabled}
        />
      </div>
    </>
  );
};

/** STAY_DESTINATIONS: city tiles with a live "N stays" count. */
const StayDestinationsForm = ({ data, onChange, disabled }: FormProps): React.JSX.Element => {
  const mode = str(data.mode) || 'manual';
  const picks = arr(data.items);
  return (
    <>
      <HeadingHint />
      <Choice
        label="Fill this row with"
        value={mode}
        options={[
          { value: 'manual', label: 'My picks' },
          { value: 'trending', label: 'Trending' },
        ]}
        onChange={(v) => onChange({ ...data, mode: v })}
        hint={
          mode === 'trending'
            ? 'Cities visitors searched stays in most. ' + LIVE_MODE_HINT
            : 'Exactly the cities you pick below, in this order.'
        }
        disabled={disabled}
      />
      <div className="grid grid-cols-2 gap-2">
        <Choice
          label="Layout"
          value={str(data.layout) || 'tiles'}
          options={[
            { value: 'tiles', label: 'Photo tiles' },
            { value: 'compact', label: 'Compact list' },
          ]}
          onChange={(v) => onChange({ ...data, layout: v })}
          disabled={disabled}
        />
        <Choice
          label="Show"
          value={str(data.scope) || 'all'}
          options={[
            { value: 'all', label: 'All' },
            { value: 'domestic', label: 'India' },
            { value: 'international', label: 'Abroad' },
          ]}
          onChange={(v) => onChange({ ...data, scope: v })}
          disabled={disabled}
        />
      </div>

      <RemoteLookup
        label="Add a city"
        placeholder="Search a city…"
        path="/stays/cities"
        toOption={(r) =>
          r.type === 'city' && typeof r.cityId === 'string'
            ? {
                id: r.cityId,
                label: str(r.name),
                description: [
                  str(r.countryName),
                  `${typeof r.hotelCount === 'number' ? r.hotelCount : 0} hotels synced`,
                ]
                  .filter(Boolean)
                  .join(' · '),
                raw: r,
              }
            : null
        }
        onPick={(o) => {
          if (picks.some((p) => p.city_id === o.id) || picks.length >= 24) return;
          onChange({
            ...data,
            items: [
              ...picks,
              {
                city_id: o.id,
                city_name: o.label,
                country_name: str(o.raw.countryName),
                image_media_id: '',
              },
            ],
          });
        }}
        disabled={disabled}
      />
      <Repeater
        items={picks}
        onItems={(items) => onChange({ ...data, items })}
        blank={() => ({ city_id: '', city_name: '', country_name: '', image_media_id: '' })}
        max={0}
        disabled={disabled}
        render={(item, set) => (
          <>
            <div className="text-sm font-medium text-(--fg)">
              {[str(item.city_name), str(item.country_name)].filter(Boolean).join(', ') ||
                'Unnamed city'}
            </div>
            <MediaDropField
              label="Tile image (optional - falls back to a hotel photo from TBO)"
              kind="image"
              value={item.image_media_id as string}
              onChange={(id) => set({ image_media_id: id })}
              disabled={disabled}
            />
          </>
        )}
      />

      <div className="grid grid-cols-3 gap-2">
        <Num
          label="Cities shown"
          value={data.max_items ?? 8}
          onChange={(v) => onChange({ ...data, max_items: v })}
          min={3}
          max={16}
          disabled={disabled}
        />
        <div className="col-span-2">
          <StayDatesFields data={data} onChange={onChange} disabled={disabled} />
        </div>
      </div>
      <p className="text-[12px] text-(--fg-faint)">
        The stay count under each city is live from the TBO hotel sync. The dates only pre-fill the
        search a tile opens.
      </p>
    </>
  );
};

const airportOption = (r: Data): LookupOption | null =>
  typeof r.iataCode === 'string' && r.iataCode.length === 3
    ? {
        id: r.iataCode,
        label: `${str(r.cityName)} (${r.iataCode})`,
        description: [str(r.airportName), str(r.countryName)].filter(Boolean).join(' · '),
        raw: r,
      }
    : null;

/** FLIGHT_DEALS: route cards priced from TBO's fare calendar. */
const FlightDealsForm = ({ data, onChange, disabled }: FormProps): React.JSX.Element => {
  const mode = str(data.mode) || 'manual';
  return (
    <>
      <HeadingHint />
      <Choice
        label="Fill this row with"
        value={mode}
        options={[
          { value: 'manual', label: 'My routes' },
          { value: 'trending', label: 'Trending' },
        ]}
        onChange={(v) => onChange({ ...data, mode: v })}
        hint={
          mode === 'trending'
            ? 'Routes visitors searched most. ' + LIVE_MODE_HINT
            : 'Exactly the routes you add below, in this order.'
        }
        disabled={disabled}
      />
      <Repeater
        items={arr(data.items)}
        onItems={(items) => onChange({ ...data, items })}
        blank={() => ({ from: '', to: '', from_label: '', to_label: '', image_media_id: '' })}
        max={24}
        disabled={disabled}
        render={(item, set) => (
          <>
            <div className="text-sm font-medium text-(--fg)">
              {str(item.from) && str(item.to)
                ? `${str(item.from_label) || str(item.from)} (${str(item.from)}) → ${str(item.to_label) || str(item.to)} (${str(item.to)})`
                : 'Pick both airports'}
            </div>
            {str(item.from) !== '' && str(item.from) === str(item.to) && (
              <p className="text-[12px] text-red-600">From and To are the same airport.</p>
            )}
            <div className="grid grid-cols-2 gap-2">
              <RemoteLookup
                label="From"
                placeholder="City or airport…"
                path="/flights/airports"
                toOption={airportOption}
                onPick={(o) => set({ from: o.id, from_label: str(o.raw.cityName) })}
                disabled={disabled}
              />
              <RemoteLookup
                label="To"
                placeholder="City or airport…"
                path="/flights/airports"
                toOption={airportOption}
                onPick={(o) => set({ to: o.id, to_label: str(o.raw.cityName) })}
                disabled={disabled}
              />
            </div>
            <MediaDropField
              label="Card image (optional - a route tile is drawn without one)"
              kind="image"
              value={item.image_media_id as string}
              onChange={(id) => set({ image_media_id: id })}
              disabled={disabled}
            />
          </>
        )}
      />
      <div className="grid grid-cols-3 gap-2">
        <Num
          label="Cards shown"
          value={data.max_cards ?? 6}
          onChange={(v) => onChange({ ...data, max_cards: v })}
          min={3}
          max={12}
          disabled={disabled}
        />
        <Num
          label="Fare window, days"
          value={data.window_days ?? 30}
          onChange={(v) => onChange({ ...data, window_days: v })}
          min={7}
          max={60}
          disabled={disabled}
        />
        {mode === 'trending' && (
          <Num
            label="Look back, days"
            value={data.trending_days ?? 14}
            onChange={(v) => onChange({ ...data, trending_days: v })}
            min={1}
            max={90}
            disabled={disabled}
          />
        )}
      </div>
      <p className="text-[12px] text-(--fg-faint)">
        Each card shows the cheapest one-way economy fare in the window and the date it falls on,
        from TBO. A route TBO has no fare for is skipped; with fewer than three routes the row hides
        itself.
      </p>
      <div className="grid grid-cols-2 gap-2">
        <Text
          label="View-all label (optional)"
          value={data.view_all_label}
          onChange={(v) => onChange({ ...data, view_all_label: v })}
          placeholder="Search all flights"
          disabled={disabled}
        />
        <Text
          label="View-all link (optional)"
          value={data.view_all_href}
          onChange={(v) => onChange({ ...data, view_all_href: v })}
          placeholder="/flights/search"
          disabled={disabled}
        />
      </div>
    </>
  );
};

/** ACTIVITY_PICKS: typed activity lists in up to three side-by-side columns. */
const ActivityPicksForm = ({ data, onChange, disabled }: FormProps): React.JSX.Element => (
  <>
    <HeadingHint />
    <Text
      label="Button label"
      value={data.cta_label}
      onChange={(v) => onChange({ ...data, cta_label: v })}
      placeholder="Enquire"
      disabled={disabled}
    />
    <Repeater
      items={arr(data.groups)}
      onItems={(groups) => onChange({ ...data, groups })}
      blank={() => ({ heading: '', items: [] })}
      max={3}
      disabled={disabled}
      render={(group, setGroup) => (
        <>
          <Text
            label="Column heading"
            value={group.heading}
            onChange={(v) => setGroup({ heading: v })}
            placeholder="Handpicked Domestic Activities"
            disabled={disabled}
          />
          <Repeater
            items={arr(group.items)}
            onItems={(items) => setGroup({ items })}
            blank={() => ({ image_media_id: '', title: '', meta: '', price_text: '', href: '' })}
            max={12}
            disabled={disabled}
            render={(item, set) => (
              <>
                <MediaDropField
                  label="Thumbnail"
                  kind="image"
                  value={item.image_media_id as string}
                  onChange={(id) => set({ image_media_id: id })}
                  disabled={disabled}
                />
                <Text
                  label="Activity name"
                  value={item.title}
                  onChange={(v) => set({ title: v })}
                  placeholder="Dubai Desert Safari with BBQ Dinner"
                  disabled={disabled}
                />
                <div className="grid grid-cols-2 gap-2">
                  <Text
                    label="Small line (place · duration)"
                    value={item.meta}
                    onChange={(v) => set({ meta: v })}
                    placeholder="Dubai · 6 hours"
                    disabled={disabled}
                  />
                  <Text
                    label="Price"
                    value={item.price_text}
                    onChange={(v) => set({ price_text: v })}
                    placeholder="₹2,900"
                    disabled={disabled}
                  />
                </div>
                <Text
                  label="Link (empty opens the enquiry popup)"
                  value={item.href}
                  onChange={(v) => set({ href: v })}
                  placeholder="/package-itinerary/…"
                  disabled={disabled}
                />
              </>
            )}
          />
        </>
      )}
    />
  </>
);

const FORMS: Record<string, (p: FormProps) => React.JSX.Element> = {
  RICH_TEXT: RichText,
  HERO: Hero,
  GALLERY: Gallery,
  SERVICE_ICONS_ROW: ServiceIconsRow,
  STAR_ENDORSEMENTS: StarEndorsements,
  DESTINATION_SHOWCASE: DestinationShowcase,
  FEATURED_CATEGORY: FeaturedCategory,
  EDITORS_PICKS: EditorsPicks,
  EXPLORE_STORIES: ExploreStories,
  CTA_BANNER: CtaBanner,
  FEATURE_LIST: FeatureList,
  CLIENT_STORIES: ClientStories,
  REVIEWS: Reviews,
  AWARDS_BLOCK: AwardsBlock,
  CALLBACK_FORM: CallbackForm,
  MICE_FORM: MiceForm,
  FAQ_LIST: FaqList,
  PARTNERS_LOGOS: PartnersLogosForm,
  MICE_ACCREDITATIONS: MiceAccreditationsForm,
  MICE_PARTNERS_MARQUEE: MicePartnersMarqueeForm,
  MICE_HERO: MiceHeroForm,
  MICE_SERVICES: MiceServicesForm,
  MICE_CASE_STUDIES: MiceCaseStudiesForm,
  MICE_DESTINATIONS: MiceDestinationsForm,
  CLIENT_STORY_CARDS: ClientStoryCardsForm,
  MICE_CLIENT_STORIES: ClientStoryCardsForm,
  PRESS_LOGOS: PressLogosForm,
  LOCATIONS: Locations,
  TEXT_BLOCK: TextBlock,
  TEAM_PROFILES: TeamProfiles,
  FULL_IMAGE: FullImage,
  FEATURE_CARDS: FeatureCards,
  PROCESS_STEPS: ProcessSteps,
  VALUE_PROPS: ValueProps,
  STATS_STRIP: StatsStrip,
  IMAGE_MARQUEE: ImageMarquee,
  OPEN_POSITIONS: OpenPositions,
  REELS_STRIP: ReelsStripForm,
  AWARDS_STATS: AwardsStatsForm,
  VIDEO_REVIEWS: VideoReviewsForm,
  TRAVELLER_REVIEWS: TravellerReviewsForm,
  HANDPICKED_STAYS: HandpickedStays,
  STAY_COLLECTION: StayCollectionForm,
  STAY_DESTINATIONS: StayDestinationsForm,
  FLIGHT_DEALS: FlightDealsForm,
  ACTIVITY_PICKS: ActivityPicksForm,
  EXPLORE_THEMES: ExploreThemes,
  QUICK_INFO: QuickInfo,
  BEST_TIME: BestTime,
  MONTH_BY_MONTH: MonthByMonth,
  TRAVEL_STYLES: TravelStyles,
  RECOMMENDED_PACKAGES: RecommendedPackages,
  PLACES_GRID: PlacesGrid,
  EVENTS_GRID: EventsGrid,
  ACTIVITIES_GRID: ActivitiesGrid,
  PACKAGE_CAROUSELS: PackageCarousels,
  VISION_MISSION: VisionMission,
  CELEBRATION: Celebration,
  CALLBACK_CHAT: CallbackChat,
  LANDING_PACKAGES: LandingPackages,
};

export const SectionForm = ({
  sectionType,
  data,
  onChange,
  disabled,
}: {
  sectionType: string;
  data: Data;
  onChange: (next: Data) => void;
  disabled?: boolean;
}): React.JSX.Element => {
  const Form = FORMS[sectionType];
  if (!Form) {
    return <p className="text-xs text-(--fg-faint)">No editor for “{sectionType}” yet.</p>;
  }
  return (
    <div className="flex flex-col gap-3">
      <Form data={data} onChange={onChange} disabled={disabled} />
    </div>
  );
};
